/*
|--------------------------------------------------------------------------
| Mixin for Lucid Models
|--------------------------------------------------------------------------
|
| Adds the required metadata and functionality to Lucid models
| which will be utilized by the resourceful controller & factory
*/
import './lucid_augmentations'
import { default as pMap } from 'p-map'
import * as guards from './type_guards'
import { Exception } from '@poppinss/utils'
import { HttpContext } from '@adonisjs/core/http'
import { joi } from '@nhtio/lucid-resourceful/joi'
import { ValidationError } from '@nhtio/validation'
import { SimplePaginator } from '@adonisjs/lucid/database'
import { luceneToLucid } from './lucene_to_lucid_translator'
import { CachingPreloader } from './utils/caching_preloader'
import { TypedEventEmitter } from '@nhtio/tiny-typed-emitter'
import { WeakCacheService } from './utils/weak_cache_service'
import { buildCacheMiddleware } from './utils/cache_middleware'
import { HttpContextFactory } from '@adonisjs/core/factories/http'
import { ACLOperationsEnum, CRUDOperationsEnum } from './constants'
import { reportRelationCacheError } from './utils/cache_error_handler'
import { reportRelationCacheEvent } from './utils/cache_event_handler'
import { GenerateModelSchemaOptions, OpenApiSchemaService } from './open_api_schema_service'
import { resolveRelationsCacheService, isRelationCacheTarget } from './utils/cache_service_resolver'
import {
  beforeCreate,
  beforeUpdate,
  afterUpdate,
  afterDelete,
  type BaseModel,
} from '@adonisjs/lucid/orm'
import {
  operationCRUDToACL,
  isString,
  stripUndefinedValuesFromObject,
  prepareFields,
  isObject,
} from './utils'
import {
  ResourcefulArrayType,
  ResourcefulBigintType,
  ResourcefulBinaryType,
  ResourcefulIntegerType,
  ResourcefulNumberType,
  ResourcefulObjectType,
  ResourcefulStringType,
  ResourcefulUnsignedIntegerType,
} from '@nhtio/lucid-resourceful/definitions'
import {
  E_FORBIDDEN,
  E_INVALID_COLUMN_ACCESS,
  E_INVALID_RESOURCEFUL_MIXIN_OPTIONS,
  E_INVALID_RESOUREFUL_INDEX_REQUEST_EXCEPTION,
  E_INVALID_RESOUREFUL_READ_RELATIONSHIP_REQUEST_EXCEPTION,
  E_MISSING_PRIMARY_KEY_EXCEPTION,
  E_RECORD_NOT_FOUND_EXCEPTION,
  E_INVALID_PAYLOAD_EXCEPTION,
  E_FORBIDDEN_PAYLOAD_EXCEPTION,
  E_INVALID_RELATIONSHIP_EXCEPTION,
  E_BULK_UPDATE_SEARCH_UNKNOWN_EXCEPTION,
  E_INVALID_AGGREGATION_FIELD,
  E_INVALID_AGGREGATION_OPERATION,
  E_RELATION_CACHE_EVICTION_FAILED,
} from '@nhtio/lucid-resourceful/errors'
import type { ApplicationService } from '@adonisjs/core/types'
import type { EventMap, Key, Listener } from '@nhtio/tiny-typed-emitter'
import type { NormalizeConstructor } from '@adonisjs/core/types/helpers'
import type { Schema, AnySchema, ObjectSchema } from '@nhtio/validation'
import type { RelationCacheCallOptions } from './utils/caching_preloader'
import type { RelationshipsContract } from '@adonisjs/lucid/types/relations'
import type { RelationsCacheErrorHandler } from './utils/cache_error_handler'
import type { RelationsCacheEventHandler } from './utils/cache_event_handler'
import type { DatabaseQueryBuilderContract } from '@adonisjs/lucid/types/querybuilder'
import type { CacheMiddlewareInput, CacheMiddlewarePipeline } from './utils/cache_middleware'
import type {
  RelationCacheEntryOptions,
  RelationsCacheServiceResolver,
} from './utils/cache_service_resolver'
import type {
  ComputedOptions,
  LucidModel,
  ModelColumnOptions,
  ModelAdapterOptions,
  ModelQueryBuilderContract,
} from '@adonisjs/lucid/types/model'
import type {
  ExternalDocumentationObject,
  PromiseAble,
  ResourcefulAccessControlFilter,
  ResourcefulColumnDefinition,
  ResourcefulComputedAccessorDefinition,
  ResourcefulDataType,
  ResourcefulGeneralAccessControlFilter,
  ResourcefulModelColumnMetaSchema,
  ResourcefulModelComputedAccessorMetaSchema,
  ResourcefulModelMetaSchema,
  ResourcefulModelOpenApiSchema,
  ResourcefulModelRelationshipMetaSchema,
  ResourcefulPropertySchema,
  ResourcefulRelationshipDefinition,
  ResourcefulResourceAccessControlFilter,
  ValidationScoper,
  ResourcefulModelSerializableAttributes,
} from '@nhtio/lucid-resourceful/types'

/**
 * Result object returned by the resourceful index/list operation.
 *
 * Contains paginated records along with metadata about the query execution
 * and pagination state. The records are filtered according to field-level
 * access control and only contain the requested fields.
 *
 * @template ReturnType - The shape of records after field selection and ACL filtering
 */
export interface ResourcefulIndexResult<ReturnType = any> {
  /** Array of records with only the requested and accessible fields */
  records: Array<Partial<ReturnType>>
  /** Total number of records matching the filter (before pagination) */
  total: number
  /** The current page number (1-based) */
  page: number
  /** Number of records per page */
  perPage: number
  /** Aggregation results organized by field name and operation type */
  aggregations: ResourcefulIndexAggregateResults
  /** SQL query string used for counting total records */
  countQuery: string
  /** SQL query string used for fetching the paginated records */
  recordsQuery: string
}

export interface ServableResourcefulIndexResult<ReturnType = any> extends Omit<
  ResourcefulIndexResult<ReturnType>,
  'countQuery' | 'recordsQuery'
> {}

/**
 * Function type for generating payload validation schemas based on request context.
 *
 * These functions are called during create and update operations to generate
 * context-specific validation schemas using Joi. They provide the core model-level
 * validation that applies regardless of request-specific hooks.
 *
 * @param ctx - HTTP context containing request information and authentication
 * @param app - Application service instance for accessing app-level services
 * @returns Promise resolving to a Joi schema for validating the request payload
 */
export interface ResourcefulPayloadValidatorGetter {
  (ctx: HttpContext, app: ApplicationService): PromiseAble<AnySchema>
}

/**
 * Function type for generating request-specific payload validation schemas.
 *
 * These functions are provided via hooks during create and update operations
 * to add additional validation constraints beyond the base model validation.
 * They enable request-specific validation logic based on context.
 *
 * @param ctx - HTTP context containing request information and authentication
 * @param app - Application service instance for accessing app-level services
 * @returns Promise resolving to a Joi ObjectSchema for additional validation, or null to skip
 */
export interface ResourcefulPayloadSchemaGetter {
  (ctx: HttpContext, app: ApplicationService): PromiseAble<ObjectSchema | null>
}

/**
 * Function type for applying query-level scoping to database queries.
 *
 * These callbacks are applied to queries during CRUD operations to enforce
 * data access boundaries based on request context. They modify the query
 * in-place by adding WHERE clauses, JOINs, or other constraints.
 *
 * @param ctx - HTTP context containing request information and authentication
 * @param app - Application service instance for accessing app-level services
 * @param query - Database query builder to modify with scoping constraints
 * @param model - The resourceful model class the query is being built for
 * @returns Promise that resolves when the query has been modified
 */
export interface ResourcefulQueryScopeCallback {
  (
    ctx: HttpContext,
    app: ApplicationService,
    query: DatabaseQueryBuilderContract,
    model: ResourcefulModel
  ): PromiseAble<void>
}

interface BoundResourcefulQueryScopeCallback {
  (
    uid: any,
    ctx: HttpContext,
    app: ApplicationService,
    query: DatabaseQueryBuilderContract,
    model: ResourcefulModel
  ): PromiseAble<void>
}

/**
 * Array of query scope callback functions.
 *
 * Used for operations that need to apply query-level scoping (index, read, delete).
 * Callbacks are applied in sequence to build up the complete set of access constraints.
 */
export type ResourcefulScopeHooks = ResourcefulQueryScopeCallback[]

/**
 * Array of payload validation schema getter functions.
 *
 * Used for operations that accept request payloads (create, update).
 * Each function can return additional validation constraints based on request context.
 */
export type ResourcefulValidationHooks = ResourcefulPayloadSchemaGetter[]

/**
 * Combined hook interface providing both query scoping and payload validation.
 *
 * Used by the update operation which needs both query scoping (to find the record)
 * and payload validation (to validate the update data).
 */
export interface ResourcefulHooks {
  /** Query scope callbacks for constraining database queries */
  queryScopeCallbacks: ResourcefulScopeHooks
  /** Payload validation schema getters for additional validation */
  payloadValidationSchemas: ResourcefulValidationHooks
}

export type ResourcefulMixinEventMap<
  Model extends LucidModel = LucidModel,
  ModelInstance = InstanceType<Model>,
> = EventMap<{
  'acl:error': [unknown, HttpContext, ApplicationService, ModelInstance | undefined]
  'validation:scope:error': [unknown, HttpContext, ApplicationService, string, ResourcefulDataType]
}>

/**
 * Enhanced Lucid model interface providing resourceful CRUD functionality.
 *
 * This interface extends the base LucidModel with metadata-driven CRUD operations,
 * field-level access control, query scoping, OpenAPI schema generation, and event
 * handling capabilities. Models implementing this interface gain static methods
 * for handling HTTP requests with built-in validation, pagination, filtering,
 * and comprehensive security features.
 *
 * The interface defines both metadata storage properties and operational methods
 * that work together to provide a complete resourceful API layer on top of
 * AdonisJS Lucid ORM models.
 *
 * @extends LucidModel
 *
 * @example
 * ```typescript
 * import { BaseModel, column } from '@ioc:Adonis/Lucid/Orm'
 * import { withResourceful, resourceful } from 'lucid-resourceful'
 *
 * class User extends withResourceful()(BaseModel) implements ResourcefulModel {
 *   @column({ isPrimary: true })
 *   @resourceful({ type: 'number', nullable: false })
 *   public id: number
 *
 *   @column()
 *   @resourceful({ type: 'string', nullable: false })
 *   public name: string
 *
 *   // Automatically gains all ResourcefulModel methods
 *   // User.$onResourcefulIndex, User.$onResourcefulRead, etc.
 * }
 * ```
 */
export interface ResourcefulModel extends LucidModel {
  /** The display name for this model used in API documentation and error messages */
  $resourcefulName: string

  /** The column used as the title of the model instance when displayed in a list */
  $resourcefulTitleColumn?: string

  /** Optional description for OpenAPI schema documentation */
  $resourcefulMetaDescription?: string

  /** Optional external documentation reference for OpenAPI schema */
  $resourcefulMetaExternalDocs?: ExternalDocumentationObject

  /** Optional example value for OpenAPI schema documentation */
  $resourcefulMetaExample?: string

  /** Map of column property names to their resourceful metadata definitions */
  $resourcefulColumns: Map<string, ResourcefulColumnDefinition<ResourcefulPropertySchema>>

  /** Map of relationship property names to their resourceful metadata definitions */
  $resourcefulRelationships: Map<string, ResourcefulRelationshipDefinition>

  /** Map of computed accessor property names to their resourceful metadata definitions */
  $resourcefulComputedAccessors: Map<
    string,
    ResourcefulComputedAccessorDefinition<ResourcefulPropertySchema>
  >

  /** Set of enforced preloads for the model */
  $resourcefulEnforcedPreloads: Set<string>

  /** Pre-merged per-relation cache entry options for cacheable 1:1 relations */
  $resourcefulRelationsCache: Map<string, RelationCacheEntryOptions>

  /** Model-level cache entry defaults applied when a relation cache config is `true` */
  $resourcefulRelationsCacheDefaults: RelationCacheEntryOptions

  /** Optional per-model cache resolver overriding the package-level default */
  $resourcefulRelationsCacheService?: RelationsCacheServiceResolver

  /** Optional per-model relation-cache error handler overriding the package-level default (ADR-009) */
  $resourcefulOnRelationsCacheError?: RelationsCacheErrorHandler

  /** Optional per-model relation-cache event hook overriding the package-level default */
  $resourcefulOnRelationsCacheEvent?: RelationsCacheEventHandler

  /** Pre-built per-model cache-write middleware pipeline run before encode (ADR-012) */
  $resourcefulCacheMiddlewarePrepare?: CacheMiddlewarePipeline | null

  /** Pre-built per-model cache-read middleware pipeline run after decode (ADR-012) */
  $resourcefulCacheMiddlewareConsume?: CacheMiddlewarePipeline | null

  /** Access control filter functions organized by CRUD operation type */
  $resourcefulAccessControlFilters: ResourcefulMixinOptions['accessControlFilters']

  /** Error handling strategy when ACL evaluation fails */
  $resourcefulOnACLError: ResourcefulMixinOptions['onACLError']

  /** Query scope callback functions for constraining database queries */
  $resourcefulQueryScopeCallbacks: ResourcefulMixinOptions['queryScopeCallbacks']

  /** Payload validation schema builder functions for create and update operations */
  $resourcefulPayloadValidationSchemaBuilders: ResourcefulMixinOptions['payloadValidationSchemaBuilders']

  /**
   * Registers an event listener for resourceful model events.
   *
   * This method provides a way to listen for events emitted during resourceful
   * operations such as ACL errors or validation scope errors. Useful for logging,
   * monitoring, or implementing custom error handling logic.
   *
   * @template K - The event key type from the resourceful event map
   * @param event - The name of the event to listen for ('acl:error', 'validation:scope:error')
   * @param listener - Function to call when the event is emitted
   * @returns The model class for method chaining
   *
   * @example
   * ```typescript
   * User.$onResourcefulEvent('acl:error', (error, ctx, app, instance) => {
   *   console.error('ACL error occurred:', error);
   * });
   * ```
   */
  $onResourcefulEvent<K>(
    event: Key<K, ResourcefulMixinEventMap<ResourcefulModel>>,
    listener: Listener<K, ResourcefulMixinEventMap<ResourcefulModel>>
  ): this

  /**
   * Registers a one-time event listener for resourceful model events.
   *
   * Similar to $onResourcefulEvent but the listener is automatically removed
   * after being called once. Useful for handling events that should only
   * trigger a single response.
   *
   * @template K - The event key type from the resourceful event map
   * @param event - The name of the event to listen for
   * @param listener - Function to call when the event is emitted
   * @returns The model class for method chaining
   *
   * @example
   * ```typescript
   * User.$onceResourcefulEvent('validation:scope:error', (error, ctx, app, key, datatype) => {
   *   console.warn('Validation scope error (one-time):', error);
   * });
   * ```
   */
  $onceResourcefulEvent<K>(
    event: Key<K, ResourcefulMixinEventMap<ResourcefulModel>>,
    listener: Listener<K, ResourcefulMixinEventMap<ResourcefulModel>>
  ): this

  /**
   * Removes an event listener for resourceful model events.
   *
   * Unregisters a previously registered event listener. If no listener is
   * provided, all listeners for the specified event are removed.
   *
   * @template K - The event key type from the resourceful event map
   * @param event - The name of the event to stop listening for
   * @param listener - Optional specific listener function to remove
   * @returns The model class for method chaining
   *
   * @example
   * ```typescript
   * // Remove specific listener
   * User.$offResourcefulEvent('acl:error', myErrorHandler);
   *
   * // Remove all listeners for an event
   * User.$offResourcefulEvent('acl:error');
   * ```
   */
  $offResourcefulEvent<K>(
    event: Key<K, ResourcefulMixinEventMap<ResourcefulModel>>,
    listener?: Listener<K, ResourcefulMixinEventMap<ResourcefulModel>>
  ): this

  $getAsResourcefulForContext(
    ctx: HttpContext,
    app: ApplicationService
  ): Promise<ResourcefulModelMetaSchema>

  $getResourcefulWriteValidationSchema(
    ctx: HttpContext,
    app: ApplicationService,
    forUpdate?: boolean
  ): Promise<{
    schema: ResourcefulModelOpenApiSchema
    validator: ObjectSchema<any>
  }>

  /**
   * Performs paginated listing and searching of model records with comprehensive filtering and aggregations.
   *
   * This method provides the core implementation for resourceful index/list operations,
   * supporting pagination, field selection, access control, aggregations, and Lucene query syntax for
   * filtering. It validates all inputs, applies ACL filters, executes the query with
   * proper field mapping between serialized names and database columns, and returns
   * structured results with query metadata and computed aggregations.
   *
   * @template SelectedFields - Array of field names that can be selected from the model's serializable attributes
   * @template ReturnType - The resulting type after picking the selected fields from the model's serializable attributes
   *
   * @param filter - Lucene-style query string for filtering records (e.g., "name:john AND email:*.com").
   *                 If null or undefined, defaults to empty string (no filtering).
   * @param page - The page number for pagination (must be ≥ 1). Used with perPage to calculate offset.
   * @param perPage - Number of records per page (must be ≥ 1 and ≤ 100 by default).
   * @param fields - Array of field names to include in the response. If null, undefined, or empty,
   *                 defaults to just the primary key field. Field names should use serialized names
   *                 (as they appear in API responses), not database column names.
   * @param sort - Array of sort specifications as [fieldName, direction] tuples. Field names must be
   *               marked as sortable in their column definitions.
   * @param ctx - HTTP context containing request information, authentication, and other request-scoped data.
   * @param app - Application service instance providing access to application-level services and configuration.
   * @param hooks - Optional array of query scope callbacks to apply additional filtering constraints.
   * @param aggregations - Optional object specifying aggregations to compute. Maps field names to arrays
   *                       of aggregation methods. Only fields marked as aggregatable can be aggregated.
   *
   * @returns Promise resolving to ResourcefulIndexResult containing:
   *   - `records`: Array of partial record objects with only the requested fields
   *   - `total`: Total number of records matching the filter (before pagination)
   *   - `page`: The requested page number (echoed back)
   *   - `perPage`: The requested per-page limit (echoed back)
   *   - `aggregations`: Computed aggregation results organized by field and method
   *   - `countQuery`: SQL query string used for counting total records
   *   - `recordsQuery`: SQL query string used for fetching the actual records
   *
   * @throws {E_MISSING_PRIMARY_KEY_EXCEPTION} When the model has no identifiable primary key
   * @throws {E_FORBIDDEN} When access is denied by model-level or field-level ACL filters
   * @throws {E_INVALID_COLUMN_ACCESS} When no fields are available for access after ACL filtering
   * @throws {E_INVALID_RESOUREFUL_INDEX_REQUEST_EXCEPTION} When input validation fails
   * @throws {E_INVALID_AGGREGATION_FIELD} When attempting to aggregate on non-aggregatable fields
   * @throws {E_INVALID_AGGREGATION_OPERATION} When using invalid aggregation methods
   *
   * @example
   * ```typescript
   * // Basic usage with pagination
   * const result = await User.$onResourcefulIndex(
   *   'name:john',
   *   1,
   *   10,
   *   ['id', 'name', 'email'],
   *   null,
   *   ctx,
   *   app
   * );
   *
   * // Complex filtering with date ranges and aggregations
   * const result = await User.$onResourcefulIndex(
   *   'status:active AND createdAt:[2021-01-01T00:00:00Z TO 2021-12-31T23:59:59Z]',
   *   2,
   *   25,
   *   ['id', 'name', 'totalSales'],
   *   [['createdAt', 'desc']],
   *   ctx,
   *   app,
   *   [],
   *   {
   *     totalSales: ['sum', 'avg', 'max'],
   *     orderCount: ['sum'],
   *     customerId: ['countDistinct']
   *   }
   * );
   * ```
   */
  $onResourcefulIndex(
    filter: string | null | undefined,
    page: number,
    perPage: number,
    fields: string | string[] | null | undefined,
    sort: Array<[string, 'asc' | 'desc']> | null | undefined,
    ctx: HttpContext,
    app: ApplicationService,
    hooks?: ResourcefulScopeHooks,
    aggregations?: ResourcefulIndexAggregateOptions
  ): Promise<ResourcefulIndexResult<{ [key: string]: any }>>

  /**
   * Retrieves a single model record by its unique identifier with access control.
   *
   * This method implements secure record retrieval by first applying query scope callbacks
   * to verify the record exists within the user's access scope, then fetching the full
   * model instance for field-level ACL evaluation. Only fields that pass ACL checks
   * are included in the response.
   *
   * @param uid - The unique identifier of the record to retrieve
   * @param ctx - HTTP context containing request information and authentication
   * @param app - Application service instance for accessing app-level services
   * @param hooks - Optional array of query scope callbacks to apply additional filtering constraints
   *
   * @returns Promise resolving to the record object with only accessible fields
   *
   * @throws {E_MISSING_PRIMARY_KEY_EXCEPTION} When the model has no identifiable primary key
   * @throws {E_RECORD_NOT_FOUND_EXCEPTION} When no record exists with the given ID or user lacks access
   * @throws {E_FORBIDDEN} When access is denied by model-level or field-level ACL filters
   *
   * @example
   * ```typescript
   * // Retrieve a user by ID
   * const user = await User.$onResourcefulRead(123, ctx, app);
   *
   * // With additional query scoping
   * const user = await User.$onResourcefulRead(123, ctx, app, [
   *   (ctx, app, query) => query.where('tenant_id', ctx.auth.user.tenantId)
   * ]);
   * ```
   */
  $onResourcefulRead(
    uid: number,
    ctx: HttpContext,
    app: ApplicationService,
    hooks?: ResourcefulScopeHooks
  ): Promise<any>

  /**
   * Loads a specific relationship for a model record with pagination, filtering, and access control.
   *
   * This method provides paginated relationship loading by leveraging the related model's
   * $onResourcefulIndex method with automatically generated relationship constraints.
   * It supports full filtering, sorting, and pagination capabilities while maintaining
   * proper access control and scoping.
   *
   * @param uid - The unique identifier of the parent record
   * @param relationshipKey - The name of the relationship property to load
   * @param filter - Lucene-style query string for filtering related records
   * @param page - The page number for pagination (must be ≥ 1)
   * @param perPage - Number of records per page (must be ≥ 1 and ≤ 100)
   * @param fields - Array of field names to include in the response from the related model
   * @param sort - Array of sort criteria as [field, direction] tuples
   * @param ctx - HTTP context containing request information and authentication
   * @param app - Application service instance for accessing app-level services
   * @param hooks - Optional array of additional query scope callbacks
   *
   * @returns Promise resolving to paginated relationship results
   *
   * @throws {E_MISSING_PRIMARY_KEY_EXCEPTION} When the model has no identifiable primary key
   * @throws {E_INVALID_RELATIONSHIP_EXCEPTION} When the specified relationship doesn't exist
   * @throws {E_FORBIDDEN} When access is denied by model-level or field-level ACL filters
   * @throws {E_INVALID_RESOUREFUL_INDEX_REQUEST_EXCEPTION} When input validation fails
   *
   * @example
   * ```typescript
   * // Load user's posts with filtering and pagination
   * const userPosts = await Post.$onResourcefulReadRelationship(
   *   123,                    // user ID
   *   'posts',               // relationship name
   *   'status:published',    // filter
   *   1,                     // page
   *   10,                    // perPage
   *   ['id', 'title'],       // fields
   *   [['createdAt', 'desc']], // sort
   *   ctx,
   *   app
   * );
   *
   * // Load user's skills (many-to-many)
   * const userSkills = await Skill.$onResourcefulReadRelationship(
   *   123,
   *   'skills',
   *   null,  // no filter
   *   1,
   *   50,
   *   null,  // all fields
   *   null,  // default sort
   *   ctx,
   *   app
   * );
   * ```
   */
  $onResourcefulReadRelationship(
    uid: any,
    relationshipKey: string,
    filter: string | null | undefined,
    page: number,
    perPage: number,
    fields: string | string[] | null | undefined,
    sort: Array<[string, 'asc' | 'desc']> | null | undefined,
    ctx: HttpContext,
    app: ApplicationService,
    hooks?: ResourcefulScopeHooks,
    aggregations?: ResourcefulIndexAggregateOptions
  ): Promise<ResourcefulIndexResult<{ [key: string]: any }>>

  /**
   * Creates a new model record with payload validation and access control.
   *
   * This method handles secure record creation by validating the request payload
   * against both model-level and request-specific validation schemas, checking
   * field-level write permissions, and returning the created record with appropriate
   * field filtering applied.
   *
   * @param payload - The data object containing field values for the new record
   * @param ctx - HTTP context containing request information and authentication
   * @param app - Application service instance for accessing app-level services
   * @param hooks - Optional array of validation schema getters for additional payload validation
   *
   * @returns Promise resolving to the created record with only accessible fields
   *
   * @throws {E_MISSING_PRIMARY_KEY_EXCEPTION} When the model has no identifiable primary key
   * @throws {E_INVALID_PAYLOAD_EXCEPTION} When core model validation fails
   * @throws {E_FORBIDDEN_PAYLOAD_EXCEPTION} When request-specific validation fails
   * @throws {E_FORBIDDEN} When access is denied by model-level or field-level ACL filters
   *
   * @example
   * ```typescript
   * // Create a new user
   * const user = await User.$onResourcefulCreate({
   *   name: 'John Doe',
   *   email: 'john@example.com'
   * }, ctx, app);
   *
   * // With additional validation
   * const user = await User.$onResourcefulCreate(payload, ctx, app, [
   *   (ctx, app) => joi.object({ email: joi.string().domain('company.com') })
   * ]);
   * ```
   */
  $onResourcefulCreate(
    payload: any,
    ctx: HttpContext,
    app: ApplicationService,
    hooks?: ResourcefulValidationHooks
  ): Promise<any>

  /**
   * Updates an existing model record with payload validation and access control.
   *
   * This method implements secure record updates by first verifying the record exists
   * and is accessible via the read operation, validating the update payload, checking
   * field-level write permissions, and returning the updated record with appropriate
   * field filtering applied.
   *
   * @param uid - The unique identifier of the record to update
   * @param payload - The data object containing field values to update
   * @param ctx - HTTP context containing request information and authentication
   * @param app - Application service instance for accessing app-level services
   * @param hooks - Optional object containing query scope callbacks and validation schema getters
   *
   * @returns Promise resolving to the updated record with only accessible fields
   *
   * @throws {E_MISSING_PRIMARY_KEY_EXCEPTION} When the model has no identifiable primary key
   * @throws {E_RECORD_NOT_FOUND_EXCEPTION} When no record exists with the given ID or user lacks access
   * @throws {E_INVALID_PAYLOAD_EXCEPTION} When core model validation fails
   * @throws {E_FORBIDDEN_PAYLOAD_EXCEPTION} When request-specific validation fails
   * @throws {E_FORBIDDEN} When access is denied by model-level or field-level ACL filters
   *
   * @example
   * ```typescript
   * // Update a user
   * const user = await User.$onResourcefulUpdate(123, {
   *   name: 'Jane Doe'
   * }, ctx, app);
   *
   * // With additional scoping and validation
   * const user = await User.$onResourcefulUpdate(123, payload, ctx, app, {
   *   queryScopeCallbacks: [(ctx, app, query) => query.where('active', true)],
   *   payloadValidationSchemas: [(ctx, app) => customValidationSchema]
   * });
   * ```
   */
  $onResourcefulUpdate(
    uid: number,
    payload: any,
    ctx: HttpContext,
    app: ApplicationService,
    hooks?: Partial<ResourcefulHooks>
  ): Promise<any>

  /**
   * Deletes an existing model record with access control.
   *
   * This method implements secure record deletion by applying query scope callbacks
   * to verify the record exists within the user's access scope, checking delete
   * permissions via ACL filters, and then removing the record from the database.
   *
   * @param uid - The unique identifier of the record to delete
   * @param ctx - HTTP context containing request information and authentication
   * @param app - Application service instance for accessing app-level services
   * @param hooks - Optional array of query scope callbacks to apply additional filtering constraints
   *
   * @returns Promise that resolves when the record has been successfully deleted
   *
   * @throws {E_MISSING_PRIMARY_KEY_EXCEPTION} When the model has no identifiable primary key
   * @throws {E_RECORD_NOT_FOUND_EXCEPTION} When no record exists with the given ID or user lacks access
   * @throws {E_FORBIDDEN} When access is denied by model-level ACL filters
   *
   * @example
   * ```typescript
   * // Delete a user
   * await User.$onResourcefulDelete(123, ctx, app);
   *
   * // With additional query scoping
   * await User.$onResourcefulDelete(123, ctx, app, [
   *   (ctx, app, query) => query.where('tenant_id', ctx.auth.user.tenantId)
   * ]);
   * ```
   */
  $onResourcefulDelete(
    uid: number,
    ctx: HttpContext,
    app: ApplicationService,
    hooks?: ResourcefulScopeHooks
  ): Promise<void>

  $onResourcefulBulkUpdate(
    filter: string | null | undefined,
    payload: any,
    ctx: HttpContext,
    app: ApplicationService,
    hooks?: Partial<ResourcefulHooks>
  ): Promise<
    | {
        [key: string]: any | Error
      }
    | Error
  >

  $onResourcefulBulkUpdateByUid(
    uids: (number | string)[],
    payload: any,
    ctx: HttpContext,
    app: ApplicationService,
    hooks?: Partial<ResourcefulHooks>
  ): Promise<{
    [key: string]: any | Error
  }>

  /**
   * Generates an OpenAPI schema object for this model with context-aware field filtering.
   *
   * This method creates a complete OpenAPI v3 schema representation of the model
   * by evaluating field-level access control permissions for the given request context.
   * Only fields that pass ACL checks are included in the generated schema, ensuring
   * that API documentation accurately reflects what data is accessible to the current user.
   *
   * The method processes all resourceful properties (columns, computed accessors, and
   * relationships) and converts them to their OpenAPI schema equivalents while respecting
   * access control constraints and applying proper type mappings.
   *
   * @param ctx - HTTP context containing request information and authentication
   * @param app - Application service instance for accessing app-level services
   * @param operation - The type of operation. To honor field-level ACL
   *
   * @returns Promise resolving to a complete OpenAPI schema object with:
   *   - `type`: Always 'object' for model schemas
   *   - `title`: The model's resourceful name
   *   - `description`: Optional model description from metadata
   *   - `properties`: Object containing schema definitions for accessible fields
   *   - `required`: Array of required field names (non-nullable fields)
   *   - `externalDocs`: Optional external documentation reference
   *   - `example`: Optional example value for the schema
   *
   * @example
   * ```typescript
   * // Generate OpenAPI schema for current user context
   * const schema = await User.$asOpenApiSchemaObject(ctx, app, 'read');
   *
   * // Result structure:
   * {
   *   type: 'object',
   *   title: 'User',
   *   properties: {
   *     id: { type: 'number', readOnly: true },
   *     name: { type: 'string' },
   *     email: { type: 'string', format: 'email' }
   *   },
   *   required: ['id', 'name', 'email']
   * }
   * ```
   *
   * @see {@link ResourcefulModelOpenApiSchema} for the complete schema structure
   */
  $asOpenApiSchemaObject(
    ctx: HttpContext,
    app: ApplicationService,
    operation?: 'read' | 'write',
    methodOptions?: GenerateModelSchemaOptions
  ): Promise<ResourcefulModelOpenApiSchema>

  /**
   * Creates a new model instance from HTTP context data.
   *
   * @param ctx - The HTTP context containing the request data
   * @returns A Promise that resolves to a new model instance
   */
  $createFromHttpContext(ctx: HttpContext): Promise<InstanceType<this>>
}

export const ResourcefulErrorHandlerMethod = [
  'bubble' as 'bubble',
  'pass' as 'pass',
  'fail' as 'fail',
]
export type ResourcefulErrorHandlerMethod = (typeof ResourcefulErrorHandlerMethod)[number]
export const ResourcefulACLOperationType = ['read' as 'read', 'write' as 'write']
export type ResourcefulACLOperationType = (typeof ResourcefulACLOperationType)[number]

/**
 * Supported aggregation methods for numeric fields in resourceful queries.
 *
 * These methods can be applied to fields marked as `aggregatable: true` in their
 * column definitions to compute statistical values across filtered datasets.
 *
 * @example
 * ```typescript
 * // Request aggregations on sales data
 * const result = await Order.$onResourcefulIndex('status:completed', 1, 10, ['id'], null, ctx, app, [], {
 *   totalAmount: ['sum', 'avg', 'max'],
 *   quantity: ['sum', 'countDistinct']
 * })
 * ```
 */
export const ResourcefulAggregateMethods = [
  'avg' as 'avg',
  'min' as 'min',
  'max' as 'max',
  'sum' as 'sum',
  'countDistinct' as 'countDistinct',
  'sumDistinct' as 'sumDistinct',
  'avgDistinct' as 'avgDistinct',
]
export type ResourcefulAggregateMethods = (typeof ResourcefulAggregateMethods)[number]

/**
 * Configuration object for specifying aggregations to perform on index operations.
 *
 * Maps field names to arrays of aggregation methods to compute. Only fields marked
 * as `aggregatable: true` in their column definitions can be aggregated.
 *
 * @example
 * ```typescript
 * const aggregations: ResourcefulIndexAggregateOptions = {
 *   totalSales: ['sum', 'avg', 'max', 'min'],
 *   orderCount: ['sum'],
 *   customerId: ['countDistinct']
 * }
 * ```
 */
export interface ResourcefulIndexAggregateOptions {
  [key: string]: ResourcefulAggregateMethods[]
}

/**
 * Results object containing computed aggregation values organized by field and method.
 *
 * The structure mirrors the requested aggregations, with each field containing
 * the computed values for the requested aggregation methods.
 *
 * @example
 * ```typescript
 * const results: ResourcefulIndexAggregateResults = {
 *   totalSales: { sum: 50000, avg: 125.5, max: 999.99, min: 10.00 },
 *   orderCount: { sum: 400 },
 *   customerId: { countDistinct: 150 }
 * }
 * ```
 */
export interface ResourcefulIndexAggregateResults {
  [key: string]: Partial<Record<ResourcefulAggregateMethods, number>>
}

type ResourcefulCheckAccessOptions<
  Model extends LucidModel = LucidModel,
  ResourcefulModelInstance = InstanceType<Model>,
> = { ctx: HttpContext; app: ApplicationService } & (
  | { operation: CRUDOperationsEnum.LIST | CRUDOperationsEnum.CREATE; instance?: undefined }
  | {
      operation: CRUDOperationsEnum.READ | CRUDOperationsEnum.UPDATE | CRUDOperationsEnum.DELETE
      instance: ResourcefulModelInstance
    }
)

export interface AdvancedResourcefulMixinOptions {
  propertyEvaluationConcurrency: number
  aclEvaluationConcurrency: number
}

export interface ResourcefulMixinOptions<
  Model extends LucidModel = LucidModel,
  ModelInstance = InstanceType<Model>,
> {
  name: string
  resourcefulTitleColumn?: string
  readRequiredForWrite: boolean
  accessControlFilters: {
    list: ResourcefulGeneralAccessControlFilter[]
    create: ResourcefulGeneralAccessControlFilter[]
    read: ResourcefulResourceAccessControlFilter<Model, ModelInstance>[]
    update: ResourcefulResourceAccessControlFilter<Model, ModelInstance>[]
    delete: ResourcefulResourceAccessControlFilter<Model, ModelInstance>[]
  }
  payloadValidationSchemaBuilders: {
    create: ResourcefulPayloadValidatorGetter[]
    update: ResourcefulPayloadValidatorGetter[]
  }
  onACLError: ResourcefulErrorHandlerMethod
  onValidationScopeError: ResourcefulErrorHandlerMethod
  queryScopeCallbacks: {
    list: ResourcefulQueryScopeCallback[]
    access: ResourcefulQueryScopeCallback[]
  }
  description?: string
  externalDocs?: ExternalDocumentationObject
  example?: string
  advanced: AdvancedResourcefulMixinOptions
  // allows for forcing a relationship to be preloaded even if it wouldn't normally
  // due to it being the wrong kind (i.e. many to many)
  enforcedPreloads?: string[]
  // Per-relation caching of 1:1 (belongsTo/hasOne) relations. `true` uses
  // `relationsCacheDefaults`; an object is per-relation entry options merged over
  // the defaults. Collection relations listed here are a no-op.
  relationsCache?: Record<string, boolean | RelationCacheEntryOptions>
  // Model-level cache entry defaults applied when a relation is `true` (default { ttl: '1m' }).
  relationsCacheDefaults?: RelationCacheEntryOptions
  // Optional per-model cache resolver that overrides the package-level default
  // registered via `setDefaultRelationsCacheService`. Usually omitted.
  relationsCacheService?: RelationsCacheServiceResolver
  // Optional per-model observer for runtime relation-cache failures (read/write/
  // eviction/resolution/rehydration), overriding the package-level default
  // registered via `setDefaultOnRelationsCacheError`. Observe-only (ADR-009).
  onRelationsCacheError?: RelationsCacheErrorHandler
  // Optional per-model observer for relation-cache lifecycle events (hit/miss/
  // store/force/bypass/evict), overriding the package-level default registered
  // via `setDefaultOnRelationsCacheEvent`. Lets the host route cache telemetry to
  // its own logger / metrics; the package itself imports no logger. Observe-only.
  onRelationsCacheEvent?: RelationsCacheEventHandler
  // Optional per-model cache middleware run over a relation snapshot just before it
  // is encoded and written to the cache (ADR-012). A single handler or an ordered
  // list; handlers mutate `context.value`. Applies whenever THIS model is loaded as a
  // cached 1:1 relation — e.g. encrypting sensitive fields before they reach the cache.
  cacheMiddlewarePrepare?: CacheMiddlewareInput
  // Optional per-model cache middleware run over a decoded snapshot just before it is
  // rehydrated on a cache hit (ADR-012) — the symmetric inverse of `cacheMiddlewarePrepare`.
  cacheMiddlewareConsume?: CacheMiddlewareInput
}

type ResourcefulCheckAccessReturn =
  | {
      isForbidden: true
      message: string
      allowedSerializedMap?: undefined
      allowedColumnsMap?: undefined
    }
  | {
      isForbidden: false
      message?: undefined
      allowedSerializedMap: Map<string, boolean>
      allowedColumnsMap: Map<string, boolean>
    }

const getFieldKey = (
  key: string,
  definition: ModelColumnOptions | ComputedOptions | ReturnType<LucidModel['$getRelation']>
) => {
  if (isString(definition.serializeAs)) {
    return definition.serializeAs as string | null
  }
  return key
}

function StaticImplements<T>() {
  return (_t: T) => {}
}

// Keyed as `ModelName:depth` so a partial depth=1 result (no grandchild
// relationships due to the `depth > 1` guard) never poisons the depth=0
// full result for the same model when two different callers encounter it
// in different traversal orders.
const filterablePropertiesForModelCache = new Map<string, string[]>()

const filterableLocalPropertiesForModelCache = new Map<
  LucidModel | (LucidModel & ResourcefulModel),
  string[]
>()

const getFilterableLocalPropertiesFromModel = (
  model: LucidModel | (LucidModel & ResourcefulModel)
) => {
  const cached = filterableLocalPropertiesForModelCache.get(model)
  if (cached) {
    return cached
  }
  const filterableLocalProperties: string[] = []
  if (guards.isResourcefulModel(model)) {
    model.$resourcefulColumns.forEach((definition, key) => {
      if (definition.filterable) {
        const lucidOptions = model.$getColumn(key)
        if (lucidOptions) {
          const filterableKey = getFieldKey(key, lucidOptions)
          if (filterableKey) {
            filterableLocalProperties.push(filterableKey)
          }
        }
      }
    })
  }
  const returnable = Array.from(new Set(filterableLocalProperties)) // Ensure uniqueness
  filterableLocalPropertiesForModelCache.set(model, returnable)
  return returnable
}

const getFilterablePropertiesFromModel = (
  model: LucidModel | (LucidModel & ResourcefulModel),
  depth: number = 0
): { [key: string]: any } => {
  if (depth > 1) {
    // Prevent infinite recursion in case of circular relationships
    return []
  }
  const cacheKey = `${(model as any).name}:${depth}`
  const cached = filterablePropertiesForModelCache.get(cacheKey)
  if (cached) {
    return cached
  }
  const definitions: Map<
    string,
    | ResourcefulColumnDefinition<any>
    | ResourcefulComputedAccessorDefinition<any>
    | ResourcefulRelationshipDefinition<LucidModel>
  > = new Map()
  const filterablePropertiesRaw: string[] = []
  filterablePropertiesRaw.push(...getFilterableLocalPropertiesFromModel(model))
  if (guards.isResourcefulModel(model)) {
    filterablePropertiesRaw.forEach((field) => {
      const definition =
        model.$resourcefulColumns.get(field) || model.$resourcefulComputedAccessors.get(field)
      if (definition) {
        definitions.set(field, definition)
      }
    })
    model.$resourcefulRelationships.forEach((_definition, key) => {
      const lucidOptions = model.$getRelation(key)
      if (lucidOptions) {
        const relationshipKey = getFieldKey(key, lucidOptions)
        const relatedModel = lucidOptions.relatedModel()
        if (relationshipKey && guards.isResourcefulModel(relatedModel)) {
          const relatedFilterableFields = Object.entries(
            getFilterablePropertiesFromModel(relatedModel, depth + 1)
          )
          relatedFilterableFields.forEach(([relatedField, relatedFieldDefinitionType]) => {
            const relatedFilterableField = [relationshipKey, relatedField].join('.')
            filterablePropertiesRaw.push(relatedFilterableField)
            definitions.set(relatedFilterableField, { type: relatedFieldDefinitionType } as any)
          })
        }
      }
    })
  }
  const returnable = Object.assign(
    {},
    ...Array.from(new Set(filterablePropertiesRaw)).map((k) => {
      const def = definitions.get(k)
      if (!def) {
        return { [k]: { type: 'unknown' } }
      }
      if ('type' in def) {
        return { [k]: def.type }
      }
      return { [k]: { type: 'unknown' } }
    })
  )
  filterablePropertiesForModelCache.set(cacheKey, returnable)
  return returnable
}

const capitalizeFirstLetterOfString = (str: string): string => {
  if (typeof str !== 'string' || str.length === 0) {
    return str
  }
  return str.charAt(0).toUpperCase() + str.slice(1)
}

const getAggregationResults = (
  desired: ResourcefulIndexAggregateOptions,
  results: { total: number; [key: string]: number }
): ResourcefulIndexAggregateResults => {
  const ret: ResourcefulIndexAggregateResults = {}
  Object.keys(desired).forEach((key) => {
    const methods = desired[key]
    if (methods.length > 0) {
      ret[key] = {}
      methods.forEach((method) => {
        const desiredForMethodKey = [key, capitalizeFirstLetterOfString(method)].join('')
        if (results[desiredForMethodKey] !== undefined && results[desiredForMethodKey] !== null) {
          switch (method) {
            default:
              ret[key][method] = Number(results[desiredForMethodKey])
              break
          }
        }
      })
    }
  })
  return ret
}

// A BentoCache `Duration` is `number | string | null`. Validating each cache
// option explicitly (instead of `.object().unknown(true)`) rejects typos like
// `tts` and mistyped values at model-definition time. These fields mirror the
// `RelationCacheEntryOptions` subset of BentoCache's `RawCommonOptions`.
const cacheDurationSchema = joi.alternatives().try(joi.number(), joi.string(), joi.valid(null))
const relationCacheEntryOptionsSchema = joi.object({
  ttl: cacheDurationSchema.optional(),
  grace: joi
    .alternatives()
    .try(joi.boolean().valid(false), joi.number(), joi.string(), joi.valid(null))
    .optional(),
  graceBackoff: cacheDurationSchema.optional(),
  suppressL2Errors: joi.boolean().optional(),
  timeout: cacheDurationSchema.optional(),
  hardTimeout: cacheDurationSchema.optional(),
  lockTimeout: cacheDurationSchema.optional(),
})

// `cacheMiddlewarePrepare` / `cacheMiddlewareConsume` accept a single middleware
// handler or an ordered list of handlers (ADR-012); each is a `(context, next)`
// function. They are built into an `@nhtio/middleware` pipeline after validation.
const cacheMiddlewareSchema = joi
  .alternatives()
  .try(joi.function(), joi.array().items(joi.function()))

/**
 * Creates a mixin that adds resourceful CRUD functionality to Lucid models.
 *
 * This function implements the mixin pattern to enhance AdonisJS Lucid models with
 * metadata-driven CRUD operations, field-level access control, query scoping,
 * and OpenAPI schema generation capabilities. The resulting model gains static
 * methods for handling HTTP requests with built-in validation, pagination,
 * filtering, and security features.
 *
 * The mixin validates and normalizes configuration options, creates isolated
 * metadata maps for each model class to prevent inheritance conflicts, and
 * establishes an event emitter for monitoring ACL and validation operations.
 *
 * @param options - Configuration object for customizing mixin behavior
 * @param options.name - Display name for the model (defaults to class name)
 * @param options.readRequiredForWrite - Whether read access is required before write operations
 * @param options.accessControlFilters - Object containing arrays of ACL filter functions for different operations
 * @param options.accessControlFilters.list - Filters applied during listing/search operations
 * @param options.accessControlFilters.create - Filters applied during record creation
 * @param options.accessControlFilters.read - Filters applied during record reading
 * @param options.accessControlFilters.update - Filters applied during record updates
 * @param options.accessControlFilters.delete - Filters applied during record deletion
 * @param options.onACLError - Error handling strategy when ACL evaluation fails ('throw', 'pass', 'fail')
 * @param options.onValidationScopeError - Error handling strategy when validation scope functions fail
 * @param options.queryScopeCallbacks - Object containing query scope callback functions
 * @param options.queryScopeCallbacks.list - Callbacks applied to listing/search queries
 * @param options.queryScopeCallbacks.access - Callbacks applied to individual record access queries
 * @param options.description - Optional description for OpenAPI documentation
 * @param options.externalDocs - Optional external documentation reference for OpenAPI
 * @param options.example - Optional example value for OpenAPI documentation
 * @param options.advanced - Advanced configuration options for performance tuning
 * @param options.advanced.propertyEvaluationConcurrency - Concurrency limit for property ACL evaluation
 * @param options.advanced.aclEvaluationConcurrency - Concurrency limit for ACL filter evaluation
 *
 * @returns A mixin function that accepts a Lucid model class and returns the enhanced model
 *
 * @throws {E_INVALID_RESOURCEFUL_MIXIN_OPTIONS} When the provided options fail validation
 *
 * @example
 * ```typescript
 * import { BaseModel, column } from '@ioc:Adonis/Lucid/Orm'
 * import { withResourceful, resourceful } from 'lucid-resourceful'
 *
 * class User extends withResourceful({
 *   name: 'User',
 *   readRequiredForWrite: true,
 *   accessControlFilters: {
 *     read: [(ctx) => ctx.auth.user?.id === ctx.params.id],
 *     update: [(ctx) => ctx.auth.user?.isAdmin]
 *   }
 * })(BaseModel) {
 *   @column({ isPrimary: true })
 *   @resourceful({ type: 'number', nullable: false })
 *   public id: number
 *
 *   @column()
 *   @resourceful({ type: 'string', nullable: false })
 *   public name: string
 * }
 *
 * // Generated controller with CRUD operations
 * const UserController = User.generateController()
 * ```
 *
 * @see {@link ResourcefulMixinOptions} for detailed configuration options
 * @see {@link ResourcefulModel} for the enhanced model interface
 */
export function withResourceful(
  options: Partial<ResourcefulMixinOptions> = {}
): <Model extends NormalizeConstructor<typeof BaseModel>>(
  superclass: Model
) => Model & ResourcefulModel {
  return <Model extends NormalizeConstructor<typeof BaseModel>>(
    superclass: Model
  ): Model & ResourcefulModel => {
    const optionsSchema = joi.object<ResourcefulMixinOptions>({
      name: joi.string().default(superclass.name),
      resourcefulTitleColumn: joi.string().optional(),
      readRequiredForWrite: joi.boolean().default(false),
      accessControlFilters: joi
        .object({
          list: joi.array().items(joi.function()).default([]),
          create: joi.array().items(joi.function()).default([]),
          read: joi.array().items(joi.function()).default([]),
          update: joi.array().items(joi.function()).default([]),
          delete: joi.array().items(joi.function()).default([]),
        })
        .default({}),
      onACLError: joi
        .string()
        .valid(...ResourcefulErrorHandlerMethod)
        .default('throw'),
      onValidationScopeError: joi
        .string()
        .valid(...ResourcefulErrorHandlerMethod)
        .default('throw'),
      queryScopeCallbacks: joi
        .object({
          list: joi.array().items(joi.function()).default([]),
          access: joi.array().items(joi.function()).default([]),
        })
        .default({}),
      payloadValidationSchemaBuilders: joi
        .object({
          create: joi.array().items(joi.function()).default([]),
          update: joi.array().items(joi.function()).default([]),
        })
        .default({ create: [], update: [] }),
      description: joi.string().optional(),
      externalDocs: joi
        .object<ExternalDocumentationObject>({
          description: joi.string().optional(),
          url: joi.string().uri().required(),
        })
        .optional(),
      example: joi.string().optional(),
      advanced: joi
        .object<AdvancedResourcefulMixinOptions>({
          propertyEvaluationConcurrency: joi.number().integer().min(1).default(10),
          aclEvaluationConcurrency: joi.number().integer().min(1).default(2),
        })
        .default({
          propertyEvaluationConcurrency: 10,
          aclEvaluationConcurrency: 2,
        }),
      enforcedPreloads: joi.array().items(joi.string()).optional(),
      relationsCache: joi
        .object()
        .pattern(
          joi.string(),
          joi.alternatives().try(joi.boolean(), relationCacheEntryOptionsSchema)
        )
        .optional(),
      relationsCacheDefaults: relationCacheEntryOptionsSchema.optional(),
      relationsCacheService: joi.function().optional(),
      onRelationsCacheError: joi.function().optional(),
      onRelationsCacheEvent: joi.function().optional(),
      cacheMiddlewarePrepare: cacheMiddlewareSchema.optional(),
      cacheMiddlewareConsume: cacheMiddlewareSchema.optional(),
    })

    const { error, value: validatedOptions } = optionsSchema.validate(options, {
      abortEarly: true,
    }) as { error?: ValidationError; value: ResourcefulMixinOptions<Model> }

    if (error) {
      throw new E_INVALID_RESOURCEFUL_MIXIN_OPTIONS(options.name || superclass.name, error)
    }

    // Ensure each class gets its own non-inherited static Maps
    const ensureOwnResourcefulMap = <T>(cls: any, key: string): Map<string, T> => {
      if (!Object.prototype.hasOwnProperty.call(cls, key) || !(cls[key] instanceof Map)) {
        return new Map()
      }
      return cls[key]
    }

    const resourcefulColumns = ensureOwnResourcefulMap<
      ResourcefulColumnDefinition<ResourcefulPropertySchema>
    >(superclass, '$resourcefulColumns')
    const resourcefulRelationships = ensureOwnResourcefulMap<ResourcefulRelationshipDefinition>(
      superclass,
      '$resourcefulRelationships'
    )
    const resourcefulComputedAccessors = ensureOwnResourcefulMap<
      ResourcefulComputedAccessorDefinition<ResourcefulPropertySchema>
    >(superclass, '$resourcefulComputedAccessors')

    // Pre-merge the per-relation cache config: each enabled relation resolves to
    // `relation === true ? defaults : { ...defaults, ...perRelation }`. The
    // CachingPreloader and the write-through hooks read this off the model.
    const relationsCacheDefaults: RelationCacheEntryOptions =
      validatedOptions.relationsCacheDefaults
        ? { ttl: '1m', ...validatedOptions.relationsCacheDefaults }
        : { ttl: '1m' }
    const resourcefulRelationsCache = new Map<string, RelationCacheEntryOptions>()
    if (validatedOptions.relationsCache) {
      for (const [relationName, relationCacheConfig] of Object.entries(
        validatedOptions.relationsCache
      )) {
        if (relationCacheConfig === false) {
          continue
        }
        if (relationCacheConfig === true) {
          resourcefulRelationsCache.set(relationName, { ...relationsCacheDefaults })
          continue
        }
        resourcefulRelationsCache.set(relationName, {
          ...relationsCacheDefaults,
          ...relationCacheConfig,
        })
      }
    }
    const resourcefulRelationsCacheService = validatedOptions.relationsCacheService
    const resourcefulOnRelationsCacheError = validatedOptions.onRelationsCacheError
    const resourcefulOnRelationsCacheEvent = validatedOptions.onRelationsCacheEvent
    const resourcefulCacheMiddlewarePrepare = buildCacheMiddleware(
      validatedOptions.cacheMiddlewarePrepare
    )
    const resourcefulCacheMiddlewareConsume = buildCacheMiddleware(
      validatedOptions.cacheMiddlewareConsume
    )

    const resourcefulModelEventEmitter = new TypedEventEmitter<
      ResourcefulMixinEventMap<Model & ResourcefulModel>
    >()

    /**
     * Separates payload fields into regular fields and setter fields based on resourceful metadata.
     *
     * @param payload - The payload object to separate
     * @param resourcefulCols - Map of column definitions from the model
     * @param resourcefulAccessors - Map of computed accessor definitions from the model
     * @returns Object with regularFields and setterFields
     */
    const separatePayloadFields = (
      payload: Record<string, any>,
      resourcefulCols: Map<string, any>,
      resourcefulAccessors: Map<string, any>
    ): {
      regularFields: Record<string, any>
      setterFields: Record<string, any>
    } => {
      const regularFields: Record<string, any> = {}
      const setterFields: Record<string, any> = {}

      for (const [key, value] of Object.entries(payload)) {
        // Check if it's a writable computed accessor (setter)
        const computedAccessor = resourcefulAccessors?.get(key)
        if (computedAccessor) {
          setterFields[key] = value
          continue
        }

        // Check if it's a regular column
        const column = resourcefulCols?.get(key)
        if (column) {
          regularFields[key] = value
        }

        // If not in either map, skip the field
      }

      return { regularFields, setterFields }
    }

    StaticImplements<ResourcefulModel>()
    class ResourcefulModel extends superclass {
      declare static readonly $resourcefulName: string
      declare static readonly $resourcefulTitleColumn?: string
      declare static $resourcefulMetaDescription?: string
      declare static $resourcefulMetaExternalDocs?: ExternalDocumentationObject
      declare static $resourcefulMetaExample?: string
      static $resourcefulColumns: Map<
        string,
        ResourcefulColumnDefinition<ResourcefulPropertySchema>
      > = resourcefulColumns
      static $resourcefulRelationships: Map<string, ResourcefulRelationshipDefinition> =
        resourcefulRelationships
      static $resourcefulComputedAccessors: Map<
        string,
        ResourcefulComputedAccessorDefinition<ResourcefulPropertySchema>
      > = resourcefulComputedAccessors
      declare static $resourcefulEnforcedPreloads: Set<string>
      declare static $resourcefulAccessControlFilters: ResourcefulMixinOptions['accessControlFilters']
      declare static $resourcefulOnACLError: ResourcefulMixinOptions['onACLError']
      declare static $resourcefulQueryScopeCallbacks: ResourcefulMixinOptions['queryScopeCallbacks']
      declare static $resourcefulPayloadValidationSchemaBuilders: ResourcefulMixinOptions['payloadValidationSchemaBuilders']
      static weakCacheService = new WeakCacheService()
      /** Pre-merged per-relation cache entry options for cacheable 1:1 relations. */
      static $resourcefulRelationsCache: Map<string, RelationCacheEntryOptions> =
        resourcefulRelationsCache
      /** Model-level cache entry defaults applied when a relation is `true`. */
      static $resourcefulRelationsCacheDefaults: RelationCacheEntryOptions = relationsCacheDefaults
      /** Optional per-model cache resolver overriding the package-level default. */
      static $resourcefulRelationsCacheService?: RelationsCacheServiceResolver =
        resourcefulRelationsCacheService
      /** Optional per-model relation-cache error handler overriding the package-level default (ADR-009). */
      static $resourcefulOnRelationsCacheError?: RelationsCacheErrorHandler =
        resourcefulOnRelationsCacheError
      /** Optional per-model relation-cache event hook overriding the package-level default. */
      static $resourcefulOnRelationsCacheEvent?: RelationsCacheEventHandler =
        resourcefulOnRelationsCacheEvent
      /** Pre-built per-model cache-write middleware pipeline run before encode (ADR-012). */
      static $resourcefulCacheMiddlewarePrepare?: CacheMiddlewarePipeline | null =
        resourcefulCacheMiddlewarePrepare
      /** Pre-built per-model cache-read middleware pipeline run after decode (ADR-012). */
      static $resourcefulCacheMiddlewareConsume?: CacheMiddlewarePipeline | null =
        resourcefulCacheMiddlewareConsume

      /**
       * Hook that runs before creating a new record.
       * Validates the payload using the configured payloadValidationSchemaBuilders.
       */
      @beforeCreate()
      static async $resourcefulValidateBeforeCreate(model: InstanceType<Model & ResourcefulModel>) {
        if (!(model instanceof ResourcefulModel)) {
          return null
        }

        // @ts-expect-error
        await model.constructor.$resourcefulValidatePayload(model, 'create')
      }

      /**
       * Hook that runs before updating an existing record.
       * Validates the payload using the configured payloadValidationSchemaBuilders.
       */
      @beforeUpdate()
      static async $resourcefulValidateBeforeUpdate(model: InstanceType<Model & ResourcefulModel>) {
        if (!(model instanceof ResourcefulModel)) {
          return null
        }

        // @ts-expect-error
        await model.constructor.$resourcefulValidatePayload(model, 'update')
      }

      /**
       * Hook that runs after a record is updated. Evicts the row's own cached
       * relation copy by its canonical identity tag. Best-effort.
       */
      @afterUpdate()
      static async $resourcefulEvictAfterUpdate(model: InstanceType<Model & ResourcefulModel>) {
        if (!(model instanceof ResourcefulModel)) {
          return null
        }
        await ResourcefulModel.$resourcefulEvictOwnIdentity(model)
      }

      /**
       * Hook that runs after a record is deleted. Evicts the row's own cached
       * relation copy by its canonical identity tag. Best-effort.
       */
      @afterDelete()
      static async $resourcefulEvictAfterDelete(model: InstanceType<Model & ResourcefulModel>) {
        if (!(model instanceof ResourcefulModel)) {
          return null
        }
        await ResourcefulModel.$resourcefulEvictOwnIdentity(model)
      }

      /**
       * Evicts the cached relation entry tagged with this row's canonical identity
       * (`{$resourcefulName}:{primaryKeyValue}`) via `deleteByTag`.
       *
       * Gated to cache TARGETS only: once core registers the default resolver,
       * EVERY resourceful write would otherwise resolve a cache and fire
       * `deleteByTag` — including hot models (e.g. `PaymentIntentTicket`) that are
       * never the related-row of any cached 1:1 relation. We early-return unless
       * this model's identity name is a registered relation-cache target (a name
       * referenced by some model's `relationsCache`, populated eagerly at preloader
       * construction), avoiding pointless L1/L2/bus writes on hot paths.
       *
       * The remaining body — cache resolution and the `deleteByTag` call — is
       * guarded: eviction is best-effort, so a resolver throw, an unreachable cache,
       * or a transient L2 error is caught and logged at `warn`, never rethrown into
       * the business write. TTL bounds the staleness window (ADR-007).
       *
       * @param model - The row that was updated or deleted.
       */
      private static async $resourcefulEvictOwnIdentity(
        model: InstanceType<Model & ResourcefulModel>
      ) {
        // Skip models that can never be a cached relation target — avoids a
        // tag-invalidation write to L1 + L2 + the cache bus on every hot write.
        if (!isRelationCacheTarget(this.$resourcefulName)) {
          return
        }
        // Hoisted so the catch can report the tag it was evicting (when known).
        let tag: string | undefined
        try {
          const cache = await resolveRelationsCacheService(
            this as unknown as LucidModel,
            this.$resourcefulRelationsCacheService
          )
          if (!cache) {
            return
          }
          const primaryKeyValue = (model as unknown as { $primaryKeyValue?: number | string })
            .$primaryKeyValue
          if (primaryKeyValue === null || primaryKeyValue === undefined) {
            return
          }
          tag = `${this.$resourcefulName}:${String(primaryKeyValue)}`
          await cache.deleteByTag({ tags: [tag] })
          reportRelationCacheEvent(this as unknown as LucidModel, 'evict', {
            model: this.$resourcefulName,
            tag,
          })
        } catch (evictionError) {
          // Eviction is an operational site (ADR-009): a resolver throw, an
          // unreachable cache, or a transient L2 error is reported through the
          // relation-cache error handler and the business write still commits.
          // TTL bounds the staleness window. (Was a bespoke best-effort `warn`.)
          reportRelationCacheError(
            this as unknown as LucidModel,
            new E_RELATION_CACHE_EVICTION_FAILED(
              { model: this.$resourcefulName, tag },
              evictionError
            )
          )
        }
      }

      /**
       * Internal method to validate payload using payloadValidationSchemaBuilders.
       * Attempts to get HttpContext, or creates a dummy context if not available.
       */
      protected static async $resourcefulValidatePayload(
        model: InstanceType<Model & ResourcefulModel>,
        operation: 'create' | 'update'
      ) {
        const validationGetters = validatedOptions.payloadValidationSchemaBuilders?.[operation]
        if (!validationGetters || validationGetters.length === 0) {
          return
        }

        // Try to get the HttpContext and app
        let ctx: HttpContext
        let app: ApplicationService
        try {
          const { default: appInstance } = await import('@adonisjs/core/services/app')
          app = appInstance

          const context = HttpContext.get()
          if (context) {
            ctx = context
          } else {
            // Create a dummy context when real context is not available
            ctx = new HttpContextFactory().create()
          }
        } catch {
          // If we can't get the app instance, skip validation
          // This allows the model to be used in environments where AdonisJS is not fully initialized
          return
        }

        // Extract the payload from the model's dirty attributes
        const payload = model.$dirty

        // Validate the payload
        const validationError = await this.$validatePayloadWithValidationGetters(
          ctx,
          app,
          payload,
          { isUpdate: operation === 'update' }
        )

        if (validationError) {
          throw new E_INVALID_PAYLOAD_EXCEPTION(validationError)
        }
      }

      /**
       * Creates a new model instance from HTTP context data with validation.
       *
       * @param ctx - The HTTP context containing the request data
       * @returns A Promise that resolves to a new validated model instance
       * @throws {ValidationError} When validation fails for any of the matching schemas
       */
      static async $createFromHttpContext(ctx: HttpContext): Promise<InstanceType<Model>> {
        const payload = ctx.request.all()
        const instance = new this()

        const { regularFields, setterFields } = separatePayloadFields(
          payload || {},
          this.$resourcefulColumns,
          this.$resourcefulComputedAccessors
        )

        // Use native .fill() for regular fields
        instance.fill(regularFields)

        // Use Object.assign for setter fields to trigger setters
        Object.assign(instance, setterFields)

        return instance as unknown as InstanceType<Model>
      }

      /**
       * Returns a query builder for this model. When the model configures
       * `relationsCache`, the builder is wired with a {@link CachingPreloader}
       * (so native `preload` caches transparently) and gains a `preloadCached`
       * method for explicit cache control (`forceLoad`, per-call overrides).
       * Models without relation caching get the unchanged builder (ADR-006).
       *
       * @param queryOptions - Optional model adapter options.
       * @returns The model query builder, cache-wired when applicable.
       */
      static query<QueryModel extends LucidModel, Result = InstanceType<QueryModel>>(
        this: QueryModel,
        queryOptions?: ModelAdapterOptions
      ): ModelQueryBuilderContract<QueryModel, Result> {
        const builder = (
          superclass as unknown as {
            query: (
              this: QueryModel,
              queryOptions?: ModelAdapterOptions
            ) => ModelQueryBuilderContract<QueryModel, Result>
          }
        ).query.call(this, queryOptions)

        const cacheConfig = (this as unknown as typeof ResourcefulModel).$resourcefulRelationsCache
        if (!cacheConfig || cacheConfig.size === 0) {
          return builder
        }

        const cachingPreloader = new CachingPreloader(this as unknown as LucidModel, cacheConfig)
        const cacheWiredBuilder = builder as unknown as {
          usePreloader: (preloader: unknown) => unknown
          constructor: { prototype: Record<string, unknown> }
        }
        cacheWiredBuilder.usePreloader(cachingPreloader)
        // Install `preloadCached` on the builder's PROTOTYPE (not as an own
        // property): Lucid's `clone()` builds a fresh `ModelQueryBuilder` and copies
        // only the preloader (`usePreloader(this.preloader.clone())`), never
        // instance-patched methods — so an own-property method would be lost on
        // `Model.query().clone().preloadCached(...)`. Reading `this.preloader`
        // dynamically lands the per-call options on the builder's OWN (possibly
        // cloned) CachingPreloader, then delegates to native `preload(name, cb)`
        // (ADR-006).
        const builderPrototype = cacheWiredBuilder.constructor.prototype
        if (typeof builderPrototype.preloadCached !== 'function') {
          builderPrototype.preloadCached = function preloadCached(
            this: {
              preloader: unknown
              preload: (name: string, cb?: (query: unknown) => void) => unknown
            },
            name: string,
            preloadCachedOptions: RelationCacheCallOptions = {},
            cb?: (query: unknown) => void
          ) {
            const preloader = this.preloader as {
              recordCallOptions?: (name: string, options: RelationCacheCallOptions) => void
            }
            if (typeof preloader?.recordCallOptions === 'function') {
              preloader.recordCallOptions(name, preloadCachedOptions)
            }
            this.preload(name, cb)
            return this
          }
        }

        return builder
      }

      /**
       * Lazily loads relationships onto the instance, mirroring Lucid's native
       * `load` but instantiating a {@link CachingPreloader} so configured 1:1
       * relations are served/refreshed through the cache. The second argument is
       * disambiguated: a function is the Lucid callback, a plain object is our
       * `{ forceLoad }` options. Both `load(name, { forceLoad })` and
       * `load(name, cb, { forceLoad })` are supported.
       *
       * NOTE: passing a callback makes the relation a plain cache BYPASS in the
       * underlying {@link CachingPreloader} (a filtering callback can't be
       * guaranteed to match the canonical cached row), so when a callback is
       * supplied any `forceLoad`/per-call cache options on that same relation are
       * effectively ignored — the relation simply loads uncached. This matches the
       * plan's deferred nested-cache scope; caching a callback-filtered 1:1 is
       * future work.
       *
       * @param relationName - The relation name, or a preloader callback.
       * @param callbackOrOptions - The Lucid callback or the cache call options.
       * @param maybeOptions - The cache call options when a callback was passed.
       */
      load = async (
        relationName: string | ((preloader: CachingPreloader) => void),
        callbackOrOptions?: ((query: unknown) => void) | RelationCacheCallOptions,
        maybeOptions?: RelationCacheCallOptions
      ): Promise<void> => {
        const ModelCtor = this.constructor as unknown as typeof ResourcefulModel & {
          $adapter: { modelClient: (instance: unknown) => { debug: boolean } }
        }
        const cacheConfig = ModelCtor.$resourcefulRelationsCache

        // No relation caching configured → defer to native lazy loading unchanged.
        if (!cacheConfig || cacheConfig.size === 0) {
          return (
            superclass.prototype as unknown as {
              load: (
                rel: string | ((preloader: unknown) => void),
                cb?: (query: unknown) => void
              ) => Promise<void>
            }
          ).load.call(
            this,
            relationName as string,
            typeof callbackOrOptions === 'function' ? callbackOrOptions : undefined
          )
        }

        const selfRow = this as unknown as {
          $isPersisted: boolean
          $sideloaded: Record<string, unknown>
          ensureIsntDeleted: () => void
        }
        selfRow.ensureIsntDeleted()
        if (!selfRow.$isPersisted) {
          throw new Exception('Cannot lazy load relationship for an unpersisted model instance')
        }

        let callback: ((query: unknown) => void) | undefined
        let callOptions: RelationCacheCallOptions | undefined
        if (typeof callbackOrOptions === 'function') {
          callback = callbackOrOptions
          callOptions = maybeOptions
        } else {
          callOptions = callbackOrOptions
        }

        const preloader = new CachingPreloader(ModelCtor as unknown as LucidModel, cacheConfig)
        if (typeof relationName === 'function') {
          relationName(preloader)
        } else {
          if (callOptions) {
            preloader.recordCallOptions(relationName, callOptions)
          }
          preloader.preload(relationName, callback)
        }

        const queryClient = ModelCtor.$adapter.modelClient(this)
        await preloader
          .sideload(selfRow.$sideloaded)
          .debug(queryClient.debug)
          .processAllForOne(this as unknown as never, queryClient as unknown as never)
      }

      /**
       * Merges HTTP context data into the current model instance.
       *
       * @param ctx - The HTTP context containing the request data to merge
       * @returns A Promise that resolves to the current model instance after merging and validation
       * @throws {ValidationError} When validation fails for any of the matching schemas
       */
      $mergeFromHttpContext(ctx: HttpContext): this {
        const payload = ctx.request.all()

        const { regularFields, setterFields } = separatePayloadFields(
          payload || {},
          // @ts-expect-error
          this.constructor.$resourcefulColumns,
          // @ts-expect-error
          this.constructor.$resourcefulComputedAccessors
        )

        // Use native .merge() for regular fields
        this.merge(regularFields as any)

        // Use Object.assign for setter fields to trigger setters
        Object.assign(this, setterFields)

        return this
      }

      static $onResourcefulEvent<K>(
        event: Key<K, ResourcefulMixinEventMap<Model & ResourcefulModel>>,
        listener: Listener<K, ResourcefulMixinEventMap<Model & ResourcefulModel>>
      ) {
        resourcefulModelEventEmitter.on(event, listener)
        return this
      }

      static $onceResourcefulEvent<K>(
        event: Key<K, ResourcefulMixinEventMap<Model & ResourcefulModel>>,
        listener: Listener<K, ResourcefulMixinEventMap<Model & ResourcefulModel>>
      ) {
        resourcefulModelEventEmitter.once(event, listener)
        return this
      }

      static $offResourcefulEvent<K>(
        event: Key<K, ResourcefulMixinEventMap<Model & ResourcefulModel>>,
        listener?: Listener<K, ResourcefulMixinEventMap<Model & ResourcefulModel>>
      ) {
        resourcefulModelEventEmitter.off(event, listener)
        return this
      }

      static async $getAsResourcefulForContext(
        ctx: HttpContext,
        app: ApplicationService
      ): Promise<ResourcefulModelMetaSchema> {
        const resourcefulModelMetaSchema: ResourcefulModelMetaSchema = {
          name: validatedOptions.name,
          properties: {},
          description: this.$resourcefulMetaDescription,
          externalDocs: this.$resourcefulMetaExternalDocs,
          example: this.$resourcefulMetaExample,
        }
        await pMap(
          Array.from(this.$resourcefulColumns.entries()),
          async ([propertyKey, columnDefinition]) => {
            const lucidDefinitions = this.$getColumn(propertyKey)
            /* istanbul ignore if -- @preserve */
            if (!lucidDefinitions) return
            const resourcefulModelColumnMetaSchema: ResourcefulModelColumnMetaSchema = {
              propertyKey,
              definition: columnDefinition,
              lucidDefinitions,
              ...(await this.$getPropertyCanReadWrite(
                ctx,
                app,
                propertyKey,
                lucidDefinitions,
                columnDefinition.readAccessControlFilters,
                columnDefinition.writeAccessControlFilters,
                'column'
              )),
              kind: 'column',
              validator: this.$getPropertyValidator(
                ctx,
                app,
                propertyKey,
                columnDefinition.type,
                columnDefinition.nullable,
                columnDefinition.validationScopes as any,
                'column'
              ),
              filterable: columnDefinition.filterable,
              sortable: columnDefinition.sortable,
              aggregatable: columnDefinition.aggregatable,
            }
            if (
              !resourcefulModelColumnMetaSchema.canRead &&
              !resourcefulModelColumnMetaSchema.canWrite
            )
              return
            resourcefulModelMetaSchema.properties[propertyKey] = resourcefulModelColumnMetaSchema
          },
          {
            concurrency: validatedOptions.advanced.propertyEvaluationConcurrency,
          }
        )
        await pMap(
          Array.from(this.$resourcefulComputedAccessors.entries()),
          async ([propertyKey, computedAccessorDefinition]) => {
            const lucidDefinitions = this.$getComputed(propertyKey)
            /* istanbul ignore if -- @preserve */
            if (!lucidDefinitions) return
            const resourcefulModelComputedAccessorMetaSchema: ResourcefulModelComputedAccessorMetaSchema =
              {
                propertyKey,
                definition: computedAccessorDefinition,
                lucidDefinitions,
                ...(await this.$getPropertyCanReadWrite(
                  ctx,
                  app,
                  propertyKey,
                  lucidDefinitions,
                  computedAccessorDefinition.readAccessControlFilters,
                  computedAccessorDefinition.writeAccessControlFilters,
                  'computedAccessor',
                  computedAccessorDefinition.writable
                )),
                kind: 'computedAccessor',
                validator: this.$getPropertyValidator(
                  ctx,
                  app,
                  propertyKey,
                  computedAccessorDefinition.type,
                  computedAccessorDefinition.nullable,
                  computedAccessorDefinition.validationScopes as any,
                  'computedAccessor',
                  computedAccessorDefinition.writable
                ),
              }
            /* istanbul ignore if -- @preserve */
            if (
              !resourcefulModelComputedAccessorMetaSchema.canRead &&
              !resourcefulModelComputedAccessorMetaSchema.canWrite
            )
              return
            resourcefulModelMetaSchema.properties[propertyKey] =
              resourcefulModelComputedAccessorMetaSchema
          },
          {
            concurrency: validatedOptions.advanced.propertyEvaluationConcurrency,
          }
        )
        await pMap(
          Array.from(this.$resourcefulRelationships.entries()),
          async ([propertyKey, relationshipDefinition]) => {
            const lucidDefinitions = this.$getRelation(propertyKey)
            /* istanbul ignore if -- @preserve */
            if (!lucidDefinitions) return
            const resourcefulModelRelationshipMetaSchema: ResourcefulModelRelationshipMetaSchema = {
              propertyKey,
              definition: relationshipDefinition,
              lucidDefinitions,
              ...(await this.$getPropertyCanReadWrite(
                ctx,
                app,
                propertyKey,
                lucidDefinitions,
                relationshipDefinition.readAccessControlFilters,
                relationshipDefinition.writeAccessControlFilters,
                'relationship'
              )),
              kind: 'relationship',
              validator: joi.forbidden(),
              relatedModel: relationshipDefinition.relatedModel,
              filterable: getFilterablePropertiesFromModel(relationshipDefinition.relatedModel()),
            }
            /* istanbul ignore if -- @preserve */
            if (
              !resourcefulModelRelationshipMetaSchema.canRead &&
              !resourcefulModelRelationshipMetaSchema.canWrite
            )
              return
            resourcefulModelMetaSchema.properties[propertyKey] =
              resourcefulModelRelationshipMetaSchema
          },
          {
            concurrency: validatedOptions.advanced.propertyEvaluationConcurrency,
          }
        )
        return resourcefulModelMetaSchema
      }

      static $getPropertyValidator(
        ctx: HttpContext,
        app: ApplicationService,
        key: string,
        datatype: ResourcefulDataType,
        nullable: boolean,
        validationScopes: ValidationScoper<AnySchema>[] | undefined,
        kind: 'column' | 'computedAccessor' | 'relationship',
        writable: boolean = false
      ): AnySchema {
        const cacheKey = `${key}-${datatype.type}-${(<any>datatype).format}-${nullable}-${validationScopes?.length}-${kind}-${writable}`
        const cachedValue = this.weakCacheService.get(cacheKey)

        if (cachedValue) {
          return cachedValue
        }

        if ('relationship' === kind || ('computedAccessor' === kind && !writable)) {
          // relationships and read-only computed accessors are not acceptable in a payload
          return joi.any().forbidden()
        }
        const baseValidator = this.$getPropertyBaseValidator(datatype, nullable, kind, writable)
        let retValidator = baseValidator
        if (Array.isArray(validationScopes) && validationScopes.length > 0) {
          validationScopes.forEach((validationScope) => {
            /* istanbul ignore if -- @preserve */
            if (retValidator !== baseValidator) return
            try {
              validationScope(baseValidator)
            } catch (err) {
              resourcefulModelEventEmitter.emit(
                'validation:scope:error',
                err,
                ctx,
                app,
                key,
                datatype
              )
              switch (validatedOptions.onValidationScopeError) {
                case 'bubble':
                  throw err
                case 'pass':
                  // do nothing, just pass the validation scope
                  break
                case 'fail':
                  retValidator = joi.any().forbidden()
                  break
              }
            }
          })
        }

        this.weakCacheService.create(cacheKey, retValidator)

        return retValidator
      }

      protected static $getPropertyBaseValidator(
        datatype: ResourcefulDataType,
        nullable: boolean,
        kind: 'column' | 'computedAccessor' | 'relationship',
        writable: boolean = false
      ): AnySchema {
        if ('relationship' === kind || ('computedAccessor' === kind && !writable)) {
          // relationships and read-only computed accessors are not acceptable in a payload
          return joi.any().forbidden()
        }
        nullable = nullable || ('boolean' === typeof datatype.nullable && datatype.nullable)
        switch (true) {
          case guards.isResourcefulDateType(datatype): {
            /*
            |--------------------------------------------------------------------------
            | Dates are a subset of the 'string' type and thus the OpenAPI validator
            | does not have any specific options for them.
            |--------------------------------------------------------------------------
            |
            | const d: ResourcefulDateType = datatype as unknown as ResourcefulDateType
            |
            */
            let r = joi.datetime()
            if (nullable) {
              r = r.allow(null)
            }
            return r
          }
          case guards.isResourcefulDateTimeType(datatype): {
            /*
            |--------------------------------------------------------------------------
            | DateTimes are a subset of the 'string' type and thus the OpenAPI validator
            | does not have any specific options for them.
            |--------------------------------------------------------------------------
            |
            | const d: ResourcefulDateTimeType = datatype as unknown as ResourcefulDateTimeType
            |
            */
            let r = joi.datetime()
            if (nullable) {
              r = r.allow(null)
            }
            return r
          }
          case guards.isResourcefulBinaryType(datatype): {
            const d: ResourcefulBinaryType = datatype as unknown as ResourcefulBinaryType
            const min = 'number' === typeof d.minLength ? Math.abs(d.minLength) : 0
            const max =
              'number' === typeof d.maxLength ? Math.abs(d.maxLength) : Number.MAX_SAFE_INTEGER
            let r = joi.string().min(min).max(max)
            if (nullable) {
              r = r.allow(null)
            }
            return r
          }
          case guards.isResourcefulStringType(datatype): {
            const d: ResourcefulStringType = datatype as unknown as ResourcefulStringType
            let r = joi.string().min(d.minLength).max(d.maxLength)
            if (d.pattern) {
              r = r.concat(r.pattern(new RegExp(d.pattern)))
            }
            if (d.enum) {
              const enumStrings = d.enum
                .map((v) => {
                  if (isObject(v)) {
                    return v.value
                  }
                  return v
                })
                .filter((v) => 'string' === typeof v) as string[]
              r = r.concat(joi.string().valid(...enumStrings))
            }
            if (d.format) {
              switch (d.format) {
                case 'alphanum':
                  r = r.concat(joi.string().alphanum())
                  break
                case 'email':
                  r = r.concat(joi.string().email())
                  break
                case 'uppercase':
                  r = r.concat(joi.string().case('upper'))
                  break
                case 'lowercase':
                  r = r.concat(joi.string().case('lower'))
                  break
                case 'creditCard':
                  r = r.concat(joi.string().creditCard())
                  break
                case 'dataUri':
                  r = r.concat(joi.string().dataUri())
                  break
                case 'domain':
                  r = r.concat(joi.string().domain())
                  break
                case 'uuid':
                case 'guid':
                  r = r.concat(joi.string().guid())
                  break
                case 'hex':
                  r = r.concat(joi.string().hex())
                  break
                case 'hostname':
                  r = r.concat(joi.string().hostname())
                  break
                case 'ip':
                  r = r.concat(joi.string().ip({ version: ['ipv4', 'ipv6', 'ipvfuture'] }))
                  break
                case 'duration':
                  r = r.concat(joi.string().isoDuration())
                  break
                case 'uri':
                  r = r.concat(joi.string().uri())
                  break
                case 'fqdn':
                  r = r.concat(joi.string().fqdn())
                  break
              }
            }
            if (nullable) {
              r = r.allow(null)
            }
            return r
          }
          case guards.isResourcefulIntegerType(datatype): {
            const d: ResourcefulIntegerType = datatype as unknown as ResourcefulIntegerType
            let r = joi.number()
            r = r.concat(joi.number().integer().multiple(d.multipleOf))
            if (d.exclusiveMaximum) {
              r = r.concat(joi.number().less(d.maximum))
            } else {
              r = r.concat(joi.number().max(d.maximum))
            }
            if (d.exclusiveMinimum) {
              r = r.concat(joi.number().greater(d.minimum))
            } else {
              r = r.concat(joi.number().min(d.minimum))
            }
            if (nullable) {
              r = r.allow(null)
            }
            return r
          }
          case guards.isResourcefulBigintType(datatype): {
            const d: ResourcefulBigintType = datatype as unknown as ResourcefulBigintType
            if ('undefined' === typeof d.minimum) {
              d.minimum = 0n
            }
            if ('undefined' === typeof d.maximum) {
              d.maximum = BigInt(Number.MAX_SAFE_INTEGER)
            }
            if ('undefined' === typeof d.multipleOf) {
              d.multipleOf = 1n
            }
            let b = joi.bigint()
            let i = joi.number().integer()
            // @ts-ignore
            b = b.concat(
              joi
                .bigint()
                .multiple('number' === typeof d.multipleOf ? BigInt(d.multipleOf) : d.multipleOf)
            )
            // @ts-ignore
            i = i.concat(
              joi
                .number()
                .multiple(
                  'bigint' === typeof d.multipleOf
                    ? Number.parseInt(d.multipleOf.toString())
                    : d.multipleOf
                )
            )
            if (d.exclusiveMaximum) {
              if (['number', 'bigint'].includes(typeof d.maximum)) {
                // @ts-ignore
                b = b.concat(
                  joi.bigint().less('number' === typeof d.maximum ? BigInt(d.maximum) : d.maximum)
                )
              }
              i = i.concat(
                joi
                  .number()
                  .less(
                    'bigint' === typeof d.maximum
                      ? Number.parseInt(d.maximum.toString())
                      : d.maximum
                  )
              )
            } else {
              if (['number', 'bigint'].includes(typeof d.maximum)) {
                // @ts-ignore
                b = b.concat(
                  joi.bigint().max('number' === typeof d.maximum ? BigInt(d.maximum) : d.maximum)
                )
              }
              i = i.concat(
                joi
                  .number()
                  .max(
                    'bigint' === typeof d.maximum
                      ? Number.parseInt(d.maximum.toString())
                      : d.maximum
                  )
              )
            }
            if (d.exclusiveMinimum) {
              // @ts-ignore
              b = b.concat(
                joi.bigint().greater('number' === typeof d.minimum ? BigInt(d.minimum) : d.minimum)
              )
              i = i.concat(
                joi
                  .number()
                  .greater(
                    'bigint' === typeof d.minimum
                      ? Number.parseInt(d.minimum.toString())
                      : d.minimum
                  )
              )
            } else {
              // @ts-ignore
              b = b.concat(
                joi.bigint().min('number' === typeof d.minimum ? BigInt(d.minimum) : d.minimum)
              )
              i = i.concat(
                joi
                  .number()
                  .min(
                    'bigint' === typeof d.minimum
                      ? Number.parseInt(d.minimum.toString())
                      : d.minimum
                  )
              )
            }
            let r = joi.alternatives(b, i)
            if (nullable) {
              r = r.allow(null)
            }
            return r
          }
          case guards.isResourcefulUnsignedIntegerType(datatype): {
            const d: ResourcefulUnsignedIntegerType =
              datatype as unknown as ResourcefulUnsignedIntegerType
            let r = joi.number().unsafe()
            r = r.concat(joi.number().integer().multiple(d.multipleOf))
            if (d.exclusiveMaximum) {
              r = r.concat(joi.number().less(d.maximum))
            } else {
              r = r.concat(joi.number().max(d.maximum))
            }
            if (d.exclusiveMinimum) {
              r = r.concat(joi.number().greater(d.minimum))
            } else {
              r = r.concat(joi.number().min(d.minimum))
            }
            if (nullable) {
              r = r.allow(null)
            }
            return r
          }
          case guards.isResourcefulNumberType(datatype): {
            const d: ResourcefulNumberType = datatype as unknown as ResourcefulNumberType
            let r = joi.number()
            r = r.concat(joi.number().multiple(d.multipleOf))
            if (d.exclusiveMaximum) {
              r = r.concat(joi.number().less(d.maximum))
            } else {
              r = r.concat(joi.number().max(d.maximum))
            }
            if (d.exclusiveMinimum) {
              r = r.concat(joi.number().greater(d.minimum))
            } else {
              r = r.concat(joi.number().min(d.minimum))
            }
            if (nullable) {
              r = r.allow(null)
            }
            return r
          }
          case guards.isResourcefulBooleanType(datatype): {
            let r = joi.boolean()
            if (nullable) {
              r = r.allow(null)
            }
            return r
          }
          case guards.isResourcefulObjectType(datatype): {
            const d: ResourcefulObjectType = datatype as unknown as ResourcefulObjectType

            // Add property validation based on the new structure with composition patterns
            const objectSchema: { [key: string]: AnySchema } = {}

            Object.entries(d.properties).forEach(([propKey, propDef]) => {
              let propValidator: AnySchema

              if ('oneOf' in propDef) {
                // oneOf composition
                const alternatives = propDef.oneOf.map((item) =>
                  this.$getPropertyBaseValidator(
                    item,
                    item.nullable || false,
                    kind,
                    !(item.readOnly || false)
                  )
                )
                propValidator = joi.alternatives(...alternatives)
              } else if ('allOf' in propDef) {
                // allOf composition
                const schemas = propDef.allOf.map((item) =>
                  this.$getPropertyBaseValidator(
                    item,
                    item.nullable || false,
                    kind,
                    !(item.readOnly || false)
                  )
                )
                propValidator = schemas.reduce((acc, schema) => acc.concat(schema), joi.any())
              } else if ('anyOf' in propDef) {
                // anyOf composition
                const alternatives = propDef.anyOf.map((item) =>
                  this.$getPropertyBaseValidator(
                    item,
                    item.nullable || false,
                    kind,
                    !(item.readOnly || false)
                  )
                )
                propValidator = joi.alternatives(...alternatives)
              } else if ('not' in propDef) {
                // not composition
                const notSchemas = propDef.not.map((item) =>
                  this.$getPropertyBaseValidator(
                    item,
                    item.nullable || false,
                    kind,
                    !(item.readOnly || false)
                  )
                )
                propValidator = joi.any()
                notSchemas.forEach((notSchema) => {
                  propValidator = propValidator.concat(
                    joi.when(notSchema, { then: joi.forbidden() })
                  )
                })
              } else {
                // Direct ResourcefulDataType
                propValidator = this.$getPropertyBaseValidator(
                  propDef,
                  propDef.nullable || false,
                  kind,
                  !(propDef.readOnly || false)
                )
                if (Array.isArray(d.required) && d.required.includes(propKey)) {
                  // If the property is required, we need to ensure it is not nullable
                  propValidator = propValidator.concat(joi.any().required())
                }
              }

              // Check if this field should be required
              if (d.required && d.required.includes(propKey)) {
                propValidator.required()
              }

              objectSchema[propKey] = propValidator
            })
            let r = joi.object(objectSchema)

            if (d.minProperties !== undefined) {
              r = r.concat(joi.object().min(d.minProperties))
            }

            if (d.maxProperties !== undefined) {
              r = r.concat(joi.object().max(d.maxProperties))
            }

            if (guards.isObject(d.additionalProperties)) {
              const additionalPropertiesType = d.additionalProperties as ResourcefulDataType
              r = r.concat(
                joi
                  .object()
                  .pattern(
                    joi.string(),
                    this.$getPropertyBaseValidator(
                      additionalPropertiesType,
                      additionalPropertiesType.nullable || false,
                      kind,
                      !(additionalPropertiesType.readOnly || false)
                    )
                  )
              )
            } else {
              r = r.concat(joi.object().unknown(true === d.additionalProperties))
            }
            if (nullable) {
              r = r.allow(null)
            }

            return r
          }
          case guards.isResourcefulArrayType(datatype): {
            const d: ResourcefulArrayType = datatype as unknown as ResourcefulArrayType
            let r = joi.array()
            // Handle different item type structures
            let itemValidator: AnySchema
            if ('oneOf' in d.items) {
              // oneOf composition
              const alternatives = d.items.oneOf.map((item) =>
                this.$getPropertyBaseValidator(
                  item,
                  item.nullable || false,
                  kind,
                  !(item.readOnly || false)
                )
              )
              itemValidator = joi.alternatives(...alternatives)
            } else if ('allOf' in d.items) {
              // allOf composition
              const schemas = d.items.allOf.map((item) =>
                this.$getPropertyBaseValidator(
                  item,
                  item.nullable || false,
                  kind,
                  !(item.readOnly || false)
                )
              )
              itemValidator = schemas.reduce((acc, schema) => acc.concat(schema), joi.any())
            } else if ('anyOf' in d.items) {
              // anyOf composition
              const alternatives = d.items.anyOf.map((item) =>
                this.$getPropertyBaseValidator(
                  item,
                  item.nullable || false,
                  kind,
                  !(item.readOnly || false)
                )
              )
              itemValidator = joi.alternatives(...alternatives)
            } else if ('not' in d.items) {
              // not composition
              const notSchemas = d.items.not.map((item) =>
                this.$getPropertyBaseValidator(
                  item,
                  item.nullable || false,
                  kind,
                  !(item.readOnly || false)
                )
              )
              itemValidator = joi.any()
              notSchemas.forEach((notSchema) => {
                itemValidator = itemValidator.concat(joi.when(notSchema, { then: joi.forbidden() }))
              })
            } else {
              // Direct ResourcefulDataType
              itemValidator = this.$getPropertyBaseValidator(
                d.items,
                d.items.nullable || false,
                kind,
                !(d.items.readOnly || false)
              )
            }
            r = r.concat(joi.array().items(itemValidator))
            if (d.minItems !== undefined) {
              r = r.concat(joi.array().min(d.minItems))
            }
            if (d.maxItems !== undefined) {
              r = r.concat(joi.array().max(d.maxItems))
            }
            if (d.uniqueItems) {
              r = r.concat(joi.array().unique())
            }
            if (nullable) {
              r = r.allow(null)
            }
            return r
          }
          default:
            return joi.any().forbidden()
        }
      }

      protected static async $getPropertyCanReadWrite(
        ctx: HttpContext,
        app: ApplicationService,
        key: string,
        lucidDefinition:
          | ModelColumnOptions
          | ComputedOptions
          | ReturnType<LucidModel['$getRelation']>,
        readAclFilters: ResourcefulAccessControlFilter[] = [],
        writeAclFilters: ResourcefulAccessControlFilter[] = [],
        kind: 'column' | 'computedAccessor' | 'relationship',
        writable: boolean = false
      ): Promise<{ canRead: boolean; canWrite: boolean }> {
        const fieldKey = getFieldKey(key, lucidDefinition)
        if (null === fieldKey) {
          // if the field key is null, that means that the field cannot be "read"
          // this is part of the baseline lucid decorator API, so we need to honor the
          // expected behavior
          if (validatedOptions.readRequiredForWrite) {
            return { canRead: false, canWrite: false }
          }
        }
        const canRead =
          null === fieldKey ? false : await this.$evaluatePropertyAccess(ctx, app, readAclFilters)
        const canWrite =
          'relationship' === kind || ('computedAccessor' === kind && !writable)
            ? false
            : await this.$evaluatePropertyAccess(
                ctx,
                app,
                writeAclFilters.length > 0 ? writeAclFilters : readAclFilters
              )
        return { canRead, canWrite }
      }

      protected static async $evaluatePropertyAccess(
        ctx: HttpContext,
        app: ApplicationService,
        acls: ResourcefulAccessControlFilter[] = []
      ): Promise<boolean> {
        if (acls.length === 0) {
          return true
        }
        const results = await pMap(
          acls,
          async (acl) => {
            try {
              return await acl(ctx, app)
            } catch (err) {
              resourcefulModelEventEmitter.emit('acl:error', err, ctx, app, undefined)
              switch (validatedOptions.onACLError) {
                case 'pass':
                  return true
                case 'fail':
                  return false
                default:
                  throw err
              }
            }
          },
          {
            concurrency: validatedOptions.advanced.aclEvaluationConcurrency,
          }
        )
        return results.every((result) => result === true)
      }

      static async $asOpenApiSchemaObject(
        ctx: HttpContext,
        app: ApplicationService,
        operation: 'read' | 'write' = 'read',
        methodOptions: GenerateModelSchemaOptions = {},
        asResourceful?: ResourcefulModelMetaSchema
      ): Promise<ResourcefulModelOpenApiSchema> {
        const normalizedMethodOptions = {
          includeRelations: methodOptions.includeRelations ?? false,
          relationsDeepLevel: methodOptions.relationsDeepLevel ?? null,
          ctx,
          app,
        }
        // Get the resourceful model metadata with ACL filtering applied
        const resourcefulModelMetaSchema =
          asResourceful || (await this.$getAsResourcefulForContext(ctx, app))
        // Create service instance and delegate to it
        const service = new OpenApiSchemaService()

        return await service.generateModelSchema(
          operation,
          resourcefulModelMetaSchema,
          normalizedMethodOptions,
          this
        )
      }

      static async $getWritePayloadValidationSchema(
        ctx: HttpContext,
        app: ApplicationService,
        forUpdate: boolean = false
      ) {
        const schemas: AnySchema[] = []
        await pMap(
          forUpdate
            ? validatedOptions.payloadValidationSchemaBuilders?.update || []
            : validatedOptions.payloadValidationSchemaBuilders?.create || [],
          async (builder) => {
            const schema = await builder(ctx, app)
            if (schema) {
              schemas.push(schema)
            }
          },
          { concurrency: 1 }
        )
        let ret = joi.any()
        schemas.forEach((schema) => {
          ret = ret.concat(schema)
        })
        return ret
      }

      static async $getResourcefulWriteValidationSchema(
        ctx: HttpContext,
        app: ApplicationService,
        forUpdate: boolean = false
      ) {
        const asResourceful = await this.$getAsResourcefulForContext(ctx, app)
        const schema = await this.$asOpenApiSchemaObject(ctx, app, 'write', {}, asResourceful)
        const modelJoiSchema = await this.$getWritePayloadValidationSchema(ctx, app, forUpdate)

        let validatorSchema = joi.object(
          Object.assign(
            {},
            ...Object.keys(schema.properties!)
              .filter((p) => {
                if (p === this.primaryKey) return false
                const propSchema: Record<string, any> = schema.properties![p]
                if (isObject(propSchema)) {
                  if (
                    forUpdate &&
                    (propSchema['x-resourceful-autoupdated'] === true ||
                      propSchema['x-resourceful-immutable'] === true)
                  ) {
                    return false
                  }
                  if (
                    !forUpdate &&
                    (propSchema['x-resourceful-autocreated'] === true ||
                      propSchema['x-resourceful-primary-key'] === true)
                  ) {
                    return false
                  }
                }
                return true
              })
              .map((creatablePropertyKey) => {
                let validationSchema: Schema = joi.any()

                const prop = asResourceful.properties[creatablePropertyKey]
                const schemaProp = schema.properties![creatablePropertyKey]
                if (prop) {
                  const propValidator = prop.validator
                  if (propValidator) {
                    validationSchema = validationSchema.concat(propValidator)
                  }
                }

                try {
                  const extracted = modelJoiSchema.extract(creatablePropertyKey)
                  if (extracted) {
                    validationSchema = validationSchema.concat(extracted)
                  }
                } catch {
                  // noop
                }

                const required =
                  !forUpdate &&
                  Array.isArray(schema.required) &&
                  schema.required.includes(creatablePropertyKey)
                const nullable = prop && prop.definition && prop.definition.nullable === true

                if (required) {
                  validationSchema = validationSchema.required()
                } else {
                  validationSchema = validationSchema.optional()
                }
                // Ensure that nullable is handled
                if (nullable) {
                  if (schemaProp && 'type' in schemaProp && schemaProp.type === 'string') {
                    validationSchema = validationSchema.allow(null)
                  } else {
                    validationSchema = joi.alternatives(validationSchema, joi.valid(null))
                  }
                }

                return {
                  [creatablePropertyKey]: validationSchema,
                }
              })
          )
        )

        // Add custom rules from the Model Schema
        if (
          // @ts-expect-error
          modelJoiSchema._rules &&
          // @ts-expect-error
          Array.isArray(modelJoiSchema._rules) &&
          // @ts-expect-error
          modelJoiSchema._rules.length
        ) {
          // Add .custom() rules
          // @ts-expect-error
          modelJoiSchema._rules.forEach((rule: any) => {
            if (
              rule.name === 'custom' &&
              rule.method === 'custom' &&
              typeof rule.args.method === 'function'
            ) {
              validatorSchema = validatorSchema.custom(rule.args.method)
              return
            }
          })
        }

        // Add dependency rules from the Model Schema
        if (modelJoiSchema.$_terms?.dependencies?.length) {
          modelJoiSchema.$_terms?.dependencies.forEach((dep: any) => {
            // Add .or() rule
            if (
              dep.rel === 'or' &&
              Array.isArray(dep.paths) &&
              dep.paths.every((path: unknown): path is string => typeof path === 'string')
            ) {
              validatorSchema = validatorSchema.or(...dep.paths)
              return
            }
          })
        }

        return {
          schema,
          validator: validatorSchema,
        }
      }

      protected static async $resourcefulCheckAccess(
        config: ResourcefulCheckAccessOptions
      ): Promise<ResourcefulCheckAccessReturn> {
        const { ctx, app, operation, instance } = config
        const allowedSerializedMap = new Map<string, boolean>()
        const allowedColumnsMap = new Map<string, boolean>()
        const mixinAccessControlFilters = validatedOptions.accessControlFilters[operation]

        if (mixinAccessControlFilters) {
          const mixinAccessControlResult = await Promise.all(
            mixinAccessControlFilters.map((func) => func(ctx, app, instance as any))
          )
          const isAllowedByMixinFilters = mixinAccessControlResult.every(Boolean)

          if (!isAllowedByMixinFilters) {
            return {
              isForbidden: true,
              message: 'Access denied by Mixin ACLs.',
              allowedSerializedMap: undefined,
            }
          }
        }

        // If the operation is DELETE, we don't check the field-level access
        if (operation === CRUDOperationsEnum.DELETE) {
          return {
            isForbidden: false,
            allowedSerializedMap: allowedSerializedMap,
            allowedColumnsMap,
          }
        }

        const columnsOptions = this.$resourcefulColumns.values()
        const aclOperation = operationCRUDToACL(operation)
        const addColumnOptionToallowedSerializedMap = (propertyKey: string) => {
          const serializedName = this.$keys.attributesToSerialized.resolve(propertyKey)
          const columnName = this.$keys.serializedToColumns.resolve(serializedName)

          // For write operations, we need to allow fields even if serializeAs is null
          // In this case, use the column name as the key for the allowed map
          if (aclOperation === ACLOperationsEnum.WRITE && serializedName === null) {
            // For write operations with serializeAs: null, use the column name
            if (columnName) {
              allowedSerializedMap.set(columnName, true)
              allowedColumnsMap.set(columnName, true)
            }
          } else if ('string' === typeof serializedName) {
            allowedSerializedMap.set(serializedName, true)
            if (columnName) {
              allowedColumnsMap.set(columnName, true)
            }
          }
        }

        for (const columnOptions of columnsOptions) {
          const propertyACLFilters =
            aclOperation === ACLOperationsEnum.READ
              ? columnOptions.readAccessControlFilters
              : columnOptions.writeAccessControlFilters

          // If a column has no ACLs - it allowed to everyone
          if (!propertyACLFilters) {
            addColumnOptionToallowedSerializedMap(columnOptions.propertyKey)

            continue
          }

          const isAllowed = await this.$evaluatePropertyAccess(ctx, app, propertyACLFilters)

          if (isAllowed) {
            addColumnOptionToallowedSerializedMap(columnOptions.propertyKey)
          }
        }

        // Process computed properties for READ operations
        if (aclOperation === ACLOperationsEnum.READ) {
          const computedAccessorsOptions = this.$resourcefulComputedAccessors.values()

          for (const computedAccessorOptions of computedAccessorsOptions) {
            const propertyACLFilters = computedAccessorOptions.readAccessControlFilters

            // If a computed accessor has no ACLs - it's allowed to everyone
            if (!propertyACLFilters) {
              // For computed properties, use the property key directly as the serialized name
              allowedSerializedMap.set(computedAccessorOptions.propertyKey, true)
              continue
            }

            const isAllowed = await this.$evaluatePropertyAccess(ctx, app, propertyACLFilters)

            if (isAllowed) {
              allowedSerializedMap.set(computedAccessorOptions.propertyKey, true)
            }
          }

          // Process relationships for READ operations
          const relationshipOptions = this.$resourcefulRelationships.values()

          for (const relationshipOption of relationshipOptions) {
            const propertyACLFilters = relationshipOption.readAccessControlFilters

            // If a relationship has no ACLs - it's allowed to everyone
            if (!propertyACLFilters) {
              // For relationships, use the property key directly as the serialized name
              allowedSerializedMap.set(relationshipOption.propertyKey, true)
              continue
            }

            const isAllowed = await this.$evaluatePropertyAccess(ctx, app, propertyACLFilters)

            if (isAllowed) {
              allowedSerializedMap.set(relationshipOption.propertyKey, true)
            }
          }
        }

        const hasNoAllowedFields = !allowedSerializedMap.size

        if (hasNoAllowedFields) {
          return {
            isForbidden: true,
            message: 'Access denied since user does not has access to at least 1 column.',
          }
        }

        return {
          isForbidden: false,
          allowedSerializedMap,
          allowedColumnsMap,
        }
      }

      private static $getPrivateKeyAttribute() {
        let ret: string | undefined
        this.$columnsDefinitions.forEach((columnOptions, attribute) => {
          if (
            columnOptions.serializeAs !== null &&
            (columnOptions.isPrimary || columnOptions.columnName === this.primaryKey)
          ) {
            ret = columnOptions.serializeAs || attribute
          }
        })
        return ret
      }

      static $loadPossibilitiesForResourcefulRecord() {
        const allRelationships = Array.from(this.$resourcefulRelationships.entries())
          .filter(([propertyKey]) => {
            // Only load relationships that are allowed by ACL for ALL records
            const relation = this.$getRelation(propertyKey)
            return !relation ? false : true
          })
          .map(([propertyKey]) => propertyKey)
        const possibleRelationships = Array.from(this.$resourcefulRelationships.entries())
          .filter(([propertyKey]) => {
            // Only load relationships that are allowed by ACL for ALL records
            const relation = this.$getRelation(propertyKey)
            if (!relation) return false
            if (relation.serializeAs === null) return false
            // Check if it's a 1:1 relationship type
            return relation.type === 'belongsTo' || relation.type === 'hasOne'
          })
          .map(([propertyKey]) => propertyKey)
        const possibleColumns = Array.from(this.$resourcefulColumns.entries())
          .map(([propertyKey]) => propertyKey)
          .filter((propertyKey) => {
            const lucidColumnOptions = this.$getColumn(propertyKey)
            if (!lucidColumnOptions) return false
            if (lucidColumnOptions.serializeAs === null) return false
            return lucidColumnOptions.serializeAs !== null
          })
        const possibleComputed = Array.from(this.$resourcefulComputedAccessors.entries())
          .map(([propertyKey]) => propertyKey)
          .filter((propertyKey) => {
            const lucidComputedOptions = this.$getComputed(propertyKey)
            if (!lucidComputedOptions) return false
            if (lucidComputedOptions.serializeAs === null) return false
            return lucidComputedOptions.serializeAs !== null
          })
        const possibleFields = Array.from(
          new Set([...possibleColumns, ...possibleComputed, ...possibleRelationships])
        )
          .map((prop) => this.$keys.attributesToSerialized.get(prop) || prop)
          .filter((s) => 'string' === typeof s)
        return {
          allRelationships,
          possibleRelationships,
          possibleColumns,
          possibleComputed,
          possibleFields,
        }
      }

      /**
       * Performs paginated listing and searching of model records with comprehensive filtering.
       *
       * This method provides the core implementation for resourceful index/list operations,
       * supporting pagination, field selection, access control, and Lucene query syntax for
       * filtering. It validates all inputs, applies ACL filters, executes the query with
       * proper field mapping between serialized names and database columns, and returns
       * structured results with query metadata.
       *
       * @template SelectedFields - Array of field names that can be selected from the model's serializable attributes
       * @template ReturnType - The resulting type after picking the selected fields from the model's serializable attributes
       *
       * @param filter - Lucene-style query string for filtering records (e.g., "name:john AND email:*.com").
       *                 If null or undefined, defaults to empty string (no filtering).
       * @param page - The page number for pagination (must be ≥ 1). Used with perPage to calculate offset.
       * @param perPage - Number of records per page (must be ≥ 1 and ≤ 100 by default).
       * @param fields - Array of field names to include in the response. If null, undefined, or empty,
       *                 defaults to just the primary key field. Field names should use serialized names
       *                 (as they appear in API responses), not database column names.
       * @param ctx - HTTP context containing request information, authentication, and other request-scoped data.
       * @param app - Application service instance providing access to application-level services and configuration.
       * @param hooks - Optional array of query scope callbacks to apply additional filtering constraints.
       *
       * @returns Promise resolving to ResourcefulIndexResult containing:
       *   - `records`: Array of partial record objects with only the requested fields
       *   - `total`: Total number of records matching the filter (before pagination)
       *   - `page`: The requested page number (echoed back)
       *   - `perPage`: The requested per-page limit (echoed back)
       *   - `countQuery`: SQL query string used for counting total records
       *   - `recordsQuery`: SQL query string used for fetching the actual records
       *
       * @throws {E_MISSING_PRIMARY_KEY_EXCEPTION} When the model has no identifiable primary key
       * @throws {E_FORBIDDEN} When access is denied by model-level or field-level ACL filters
       * @throws {E_INVALID_COLUMN_ACCESS} When no fields are available for access after ACL filtering
       * @throws {E_INVALID_RESOUREFUL_INDEX_REQUEST_EXCEPTION} When input validation fails
       *
       * @example
       * ```typescript
       * // Basic usage with pagination
       * const result = await User.$onResourcefulIndex(
       *   'name:john',
       *   1,
       *   10,
       *   ['id', 'name', 'email'],
       *   ctx,
       *   app
       * );
       *
       * // Complex filtering with date ranges
       * const result = await User.$onResourcefulIndex(
       *   'name:john AND createdAt:[2021-01-01T00:00:00Z TO 2021-12-31T23:59:59Z]',
       *   2,
       *   25,
       *   ['id', 'name', 'createdAt'],
       *   ctx,
       *   app
       * );
       * ```
       */
      protected static $formatResourcefulRecord(
        record: Model & ResourcefulModel,
        possibleFields: string[],
        enforcedPossibleFields: string[] = [],
        deepRelatedFieldsMap?: Map<string, string[]>
      ): Partial<ResourcefulModelSerializableAttributes<InstanceType<Model & ResourcefulModel>>> {
        if (null === record || undefined === record) {
          return record
        }
        const result: Partial<
          ResourcefulModelSerializableAttributes<InstanceType<Model & ResourcefulModel>>
        > = {}
        const { possibleFields: allPossibleFields } = this.$loadPossibilitiesForResourcefulRecord()
        possibleFields = possibleFields.filter(
          (field) => allPossibleFields.includes(field) || enforcedPossibleFields.includes(field)
        )
        possibleFields.forEach((field) => {
          const attribute = this.$keys.serializedToAttributes.get(field) || field
          if (!attribute) return
          const value = record[attribute as keyof typeof record] as any
          if (value !== undefined) {
            const resourcefulRelationshipOptions = this.$resourcefulRelationships.get(attribute)
            if (resourcefulRelationshipOptions) {
              // if the resulting value is a relationship, we need to use the related model's $formatResourcefulRecord
              const relatedModel = resourcefulRelationshipOptions.relatedModel() as any
              if (
                'function' === typeof relatedModel.$formatResourcefulRecord &&
                'function' === typeof relatedModel.$loadPossibilitiesForResourcefulRecord
              ) {
                const {
                  possibleFields: relatedPossibleFields,
                  possibleRelationships: relatedPossibleRelationships,
                } = relatedModel.$loadPossibilitiesForResourcefulRecord()
                // Deep serialization is opt-in per relationship key.
                //
                // If dot-notation was used for this relationship (e.g. 'user.country'):
                //   - Only serialize scalars + the explicitly requested nested relationships.
                //   - Pass a depth-capping map into the recursive call so those nested
                //     relationships serialize only their own scalars (no further recursion).
                //
                // If no dot-notation was used for this relationship (legacy path):
                //   - Use all relatedPossibleFields and pass no map, preserving the
                //     original recursive serialization behavior.
                const requestedNestedRelations = deepRelatedFieldsMap?.get(field)
                let fieldsForRelated: string[]
                let nestedDeepRelatedFieldsMap: Map<string, string[]> | undefined
                if (requestedNestedRelations) {
                  // Constrain to scalars + explicitly requested nested relationships
                  fieldsForRelated = relatedPossibleFields.filter(
                    (f: string) =>
                      !relatedPossibleRelationships.includes(f) ||
                      requestedNestedRelations.includes(f)
                  )
                  // Cap recursion: each requested nested relationship serializes scalars only
                  nestedDeepRelatedFieldsMap = new Map(
                    requestedNestedRelations.map((r: string) => [r, []])
                  )
                } else {
                  // Legacy behavior: serialize all possible fields, no map
                  fieldsForRelated = relatedPossibleFields
                  nestedDeepRelatedFieldsMap = undefined
                }
                result[
                  field as keyof ResourcefulModelSerializableAttributes<
                    InstanceType<Model & ResourcefulModel>
                  >
                ] = relatedModel.$formatResourcefulRecord(
                  value,
                  fieldsForRelated,
                  [],
                  nestedDeepRelatedFieldsMap
                )
              } else if ('function' === typeof value.serialize) {
                result[
                  field as keyof ResourcefulModelSerializableAttributes<
                    InstanceType<Model & ResourcefulModel>
                  >
                ] = value.serialize()
              }
            } else {
              result[
                field as keyof ResourcefulModelSerializableAttributes<
                  InstanceType<Model & ResourcefulModel>
                >
              ] = value
            }
          }
        })

        return stripUndefinedValuesFromObject(result) as ResourcefulModelSerializableAttributes<
          InstanceType<Model & ResourcefulModel>
        >
      }

      protected static async $validatePayloadWithValidationGetters(
        ctx: HttpContext,
        app: ApplicationService,
        payload: unknown,
        methodOptions?: { isUpdate: boolean }
      ): Promise<ValidationError | null> {
        const { isUpdate } = methodOptions || {}
        const { validator } = await this.$getResourcefulWriteValidationSchema(ctx, app, isUpdate)

        const safeValidate = async (): Promise<null | ValidationError> => {
          try {
            await validator
              .db(this.$adapter.modelConstructorClient(this) as any)
              .validateAsync(payload, { abortEarly: false })

            return null
          } catch (err) {
            return err as unknown as ValidationError
          }
        }

        const validationError = await safeValidate()

        if (validationError) {
          // If it's alternatives.all error, it doesn't have relevant error message and details. So we extract them manually.
          if (
            validationError.details?.length === 1 &&
            validationError.details[0].type === 'alternatives.all'
          ) {
            const alternativesError = validationError.details[0]

            if (alternativesError.context?.details.length) {
              validationError.details = alternativesError.context.details
              validationError.message = alternativesError.context.details[0].message
            }
          }

          return validationError
        }

        return null
      }

      protected static async $validatePayloadWithPayloadSchemaGetters(
        ctx: HttpContext,
        app: ApplicationService,
        payload: unknown,
        payloadSchemaGetters: ResourcefulPayloadSchemaGetter[]
      ) {
        const resolved = await Promise.all(payloadSchemaGetters.map((getter) => getter(ctx, app)))
        const schemas = resolved.filter((schema) => schema !== null && schema !== undefined)

        for (const schema of schemas) {
          const { error: schemaError } = schema.validate(payload)

          if (schemaError) {
            return schemaError
          }
        }

        return null
      }

      protected static async $resolveStorablePayload(
        ctx: HttpContext,
        app: ApplicationService,
        payload: unknown,
        validationGetters: ResourcefulPayloadValidatorGetter[],
        payloadSchemaGetters: ResourcefulPayloadSchemaGetter[]
      ) {
        const schemas: Schema[] = []
        const [schemasFromValidationGetters, schemasFromPayloadSchemaGetters] = await Promise.all([
          pMap(validationGetters, async (getter) => await getter(ctx, app), { concurrency: 5 }),
          pMap(payloadSchemaGetters, async (getter) => await getter(ctx, app), { concurrency: 5 }),
        ])
        schemasFromValidationGetters
          .filter((s) => s !== null && s !== undefined)
          .forEach((s) => {
            schemas.push(s)
          })
        schemasFromPayloadSchemaGetters
          .filter((s) => s !== null && s !== undefined)
          .forEach((s) => {
            schemas.push(s)
          })
        const mergables = await pMap(
          schemas,
          async (schema) => {
            try {
              return await schema.validateAsync(payload, { abortEarly: true, convert: true })
            } catch {
              return {}
            }
          },
          { concurrency: 5 }
        )
        return Object.assign({}, payload as Record<string, any>, ...mergables) as Record<
          string,
          any
        >
      }

      protected static $resourcefulGetQueryBuilder() {
        return this.$adapter.modelConstructorClient(this).query().from(this.table)
      }

      static async $onResourcefulIndex(
        filter: string | null | undefined,
        page: number,
        perPage: number,
        fields: string | string[] | null | undefined,
        sort: Array<[string, 'asc' | 'desc']> | null | undefined,
        ctx: HttpContext,
        app: ApplicationService,
        hooks: ResourcefulScopeHooks = [],
        aggregations: ResourcefulIndexAggregateOptions = {}
      ): Promise<ResourcefulIndexResult<{ [key: string]: any }>> {
        /*
        |--------------------------------------------------------------------------
        | Normalize inputs
        |--------------------------------------------------------------------------
        */
        const primaryKey = this.$getPrivateKeyAttribute()

        if (!primaryKey) {
          throw new E_MISSING_PRIMARY_KEY_EXCEPTION(this.$resourcefulName)
        }

        if (!isString(filter)) {
          filter = ''
        }

        fields = prepareFields(fields, primaryKey)

        /*
        |--------------------------------------------------------------------------
        | Check Access
        |--------------------------------------------------------------------------
        */
        const { isForbidden, allowedSerializedMap, message } = await this.$resourcefulCheckAccess({
          ctx,
          app,
          operation: CRUDOperationsEnum.LIST,
        })

        if (isForbidden) {
          throw new E_FORBIDDEN(message)
        }

        /*
        |--------------------------------------------------------------------------
        | Validate Request Details
        |--------------------------------------------------------------------------
        */
        const { allRelationships, possibleRelationships, possibleColumns, possibleComputed } =
          this.$loadPossibilitiesForResourcefulRecord()
        // filter out 1:many and many:many relationships since they are not loadable in this context
        const possibleFields = Array.from(allowedSerializedMap.keys()).filter(
          (f) =>
            possibleColumns.includes(f) ||
            possibleComputed.includes(f) ||
            !allRelationships.includes(f) ||
            possibleRelationships.includes(f)
        )
        if (possibleFields.length === 0) {
          throw new E_INVALID_COLUMN_ACCESS('No fields available for access')
        }
        /*
        |--------------------------------------------------------------------------
        | Deep related fields (dot-notation) support
        |--------------------------------------------------------------------------
        |
        | Fields like "tenantPaymentServiceProvider.tenant" request nested
        | relationship preloading. We separate them from flat fields before
        | Joi validation, validate them independently, and merge the root
        | relationships back into the flat field list so the existing
        | preload/serialization logic picks them up.
        |
        */
        /**
         * Deep related fields (dot-notation) support.
         *
         * Requesting `['id', 'user.country']` will:
         * 1. Validate that `user` is a loadable resourceful relationship
         * 2. Validate that `country` is a loadable relationship on the related model
         * 3. Preload `country` as a nested sub-query on `user`
         *
         * Only one level of dot-notation is supported (e.g. `user.country`).
         * Multi-level nesting (e.g. `user.country.regions`) is explicitly rejected.
         *
         * Note: When a root relationship is included (either explicitly or
         * auto-included via dot-notation), the related model is serialized with
         * **all** its possible fields — not just the nested fields requested via
         * dot-notation. This is consistent with flat relationship serialization
         * behavior in `$formatResourcefulRecord`.
         */
        const deepRelatedFields: Map<string, string[]> = new Map()
        const flatFieldSet: Set<string> = new Set()
        if (Array.isArray(fields)) {
          for (const f of fields) {
            const dotIndex = f.indexOf('.')
            if (dotIndex > 0) {
              const root = f.substring(0, dotIndex)
              const nested = f.substring(dotIndex + 1)
              // Reject multi-level nesting (e.g. 'user.country.regions')
              if (nested.includes('.')) {
                throw new E_INVALID_COLUMN_ACCESS(
                  `Only one level of dot-notation deep fields is supported (e.g. "${root}.${nested.split('.')[0]}"); deeper nesting like "${f}" is not supported`
                )
              }
              // Validate root is a loadable relationship
              if (!possibleRelationships.includes(root)) {
                throw new E_INVALID_COLUMN_ACCESS(
                  `Deep field "${f}" references "${root}" which is not a valid loadable relationship`
                )
              }
              // Validate nested field is valid on the related model
              const relDef = this.$resourcefulRelationships.get(root)
              if (relDef) {
                const relatedModel = relDef.relatedModel() as any
                if ('function' === typeof relatedModel.$loadPossibilitiesForResourcefulRecord) {
                  const { possibleRelationships: relPossibleRelationships } =
                    relatedModel.$loadPossibilitiesForResourcefulRecord()
                  if (!relPossibleRelationships.includes(nested)) {
                    throw new E_INVALID_COLUMN_ACCESS(
                      `Deep field "${f}" references "${nested}" which is not a valid loadable relationship on "${root}"`
                    )
                  }
                } else {
                  // Related model is not resourceful — we cannot validate nested
                  // fields, so fail closed rather than allowing unvalidated preloads
                  throw new E_INVALID_COLUMN_ACCESS(
                    `Deep field "${f}" cannot be validated: "${root}" does not reference a resourceful model`
                  )
                }
              }
              if (!deepRelatedFields.has(root)) {
                deepRelatedFields.set(root, [])
              }
              deepRelatedFields.get(root)!.push(nested)
              // Ensure the root relationship is included in flat fields
              flatFieldSet.add(root)
            } else {
              flatFieldSet.add(f)
            }
          }
          fields = Array.from(flatFieldSet)
        }
        const schema = joi.object({
          filter: joi.string().allow('').required(),
          page: joi.number().integer().min(1).required(),
          perPage: joi.number().integer().min(1).max(100).required(),
          fields: joi
            .array()
            .items(joi.string().valid(...possibleFields))
            .required(),
        })
        const { error: methodValidationError, value: validatedMethodOptions } = schema.validate(
          {
            filter,
            page,
            perPage,
            fields,
          },
          {
            abortEarly: false,
          }
        )
        if (methodValidationError) {
          throw new E_INVALID_RESOUREFUL_INDEX_REQUEST_EXCEPTION(methodValidationError)
        }
        /*
        |--------------------------------------------------------------------------
        | Create the baseline query
        |--------------------------------------------------------------------------
        */
        let query = luceneToLucid(
          validatedMethodOptions.filter,
          this.$adapter.modelConstructorClient(this),
          this.$keys.serializedToColumns,
          primaryKey,
          this.table,
          possibleFields,
          this
        )
        /*
        |--------------------------------------------------------------------------
        | Apply Resourceful Query Scope Callbacks
        |--------------------------------------------------------------------------
        */
        for (const scopeCallback of [
          ...(this.$resourcefulQueryScopeCallbacks.list || []),
          ...hooks,
        ]) {
          await scopeCallback(ctx, app, query, this)
        }
        /*
        |--------------------------------------------------------------------------
        | Split into separate queries for count and results
        |--------------------------------------------------------------------------
        */
        const countQuery = query.clone<{ total: number; [key: string]: number }>()
        const recordsQuery = query.clone()
        const privateKeyColumn = this.$keys.attributesToColumns.get(primaryKey)!
        countQuery.count('*', 'total')
        Object.keys(aggregations).forEach((aggKey) => {
          const desired = aggregations[aggKey]
          const fieldColumn = this.$keys.serializedToColumns.get(aggKey)
          const resourcefulDefinition = this.$resourcefulColumns.get(aggKey)
          if (!fieldColumn || !resourcefulDefinition || !resourcefulDefinition.aggregatable) {
            throw new E_INVALID_AGGREGATION_FIELD(aggKey, this.$resourcefulName)
          }
          const uniqueDesired = new Set(desired)
          uniqueDesired.forEach((desiredAggregation) => {
            switch (desiredAggregation) {
              case 'avg':
                countQuery.avg(fieldColumn, [aggKey, 'Avg'].join(''))
                break
              case 'min':
                countQuery.min(fieldColumn, [aggKey, 'Min'].join(''))
                break
              case 'max':
                countQuery.max(fieldColumn, [aggKey, 'Max'].join(''))
                break
              case 'sum':
                countQuery.sum(fieldColumn, [aggKey, 'Sum'].join(''))
                break
              case 'countDistinct':
                countQuery.countDistinct(fieldColumn, [aggKey, 'CountDistinct'].join(''))
                break
              case 'sumDistinct':
                countQuery.sumDistinct(fieldColumn, [aggKey, 'SumDistinct'].join(''))
                break
              case 'avgDistinct':
                countQuery.avgDistinct(fieldColumn, [aggKey, 'AvgDistinct'].join(''))
                break
              default:
                throw new E_INVALID_AGGREGATION_OPERATION(
                  desiredAggregation,
                  this.$resourcefulName,
                  aggKey
                )
            }
          })
        })
        recordsQuery
          .select(privateKeyColumn)
          .forPage(validatedMethodOptions.page, validatedMethodOptions.perPage)

        if (Array.isArray(sort) && sort.length > 0) {
          sort.forEach(([field, direction]) => {
            const columnName = this.$keys.serializedToColumns.get(field)
            if (allowedSerializedMap.has(field) && columnName) {
              recordsQuery.orderBy(columnName, direction)
            }
          })
        }

        const countQueryQuery = countQuery.toQuery()
        const recordsQueryQuery = recordsQuery.toQuery()
        /*
        |--------------------------------------------------------------------------
        | Retrieve raw results
        |--------------------------------------------------------------------------
        */
        await this.$hooks.runner('before:paginate').run([countQuery, this])
        const [countResults, rawRecordIds] = await Promise.all([countQuery, recordsQuery])
        const totalRaw = Number(countResults?.[0]?.total)
        const rawRecordsUnsortedQuery = this.query().whereIn(
          privateKeyColumn,
          rawRecordIds.map((r) => r[privateKeyColumn])
        )
        const relationsToSelect = validatedMethodOptions.fields.filter(
          (field: string) =>
            allowedSerializedMap.has(field) && possibleRelationships.includes(field)
        )
        // Native `preload` is used here intentionally (not `preloadCached`):
        // `rawRecordsUnsortedQuery` comes from `this.query()`, which already injects the
        // CachingPreloader, so configured 1:1 relations cache transparently on this path.
        // These inline loads never need `forceLoad`, so the extra `preloadCached` channel
        // would add nothing. (A nested/deep preload passes a callback, which the
        // CachingPreloader treats as a cache bypass — that level loads uncached.)
        relationsToSelect.forEach((prop: any) => {
          const nestedRelations = deepRelatedFields.get(prop)
          if (nestedRelations && nestedRelations.length > 0) {
            // Deep preload: preload the root relationship and its nested relationships
            rawRecordsUnsortedQuery.preload(prop, (subQuery: any) => {
              for (const nested of nestedRelations) {
                subQuery.preload(nested)
              }
            })
          } else {
            rawRecordsUnsortedQuery.preload(prop)
          }
        })
        this.$resourcefulEnforcedPreloads.forEach((prop: string) => {
          if (!possibleRelationships.includes(prop)) {
            const rel = this.$getRelation(prop)
            if (rel) {
              rawRecordsUnsortedQuery.preload(prop as any)
            }
          }
        })
        const rawRecordsUnsorted = await rawRecordsUnsortedQuery
        const rawRecords = rawRecordsUnsorted.sort((a, b) => {
          const aId = a.$getAttribute(primaryKey)
          const bId = b.$getAttribute(primaryKey)
          const rawRecordIdResultsIndexA = rawRecordIds.findIndex(
            (r) => r[primaryKey].toString() === aId.toString()
          )
          const rawRecordIdResultsIndexB = rawRecordIds.findIndex(
            (r) => r[primaryKey].toString() === bId.toString()
          )
          return rawRecordIdResultsIndexA - rawRecordIdResultsIndexB
        })
        /*
        |--------------------------------------------------------------------------
        | Return results
        |--------------------------------------------------------------------------
        */
        const returnableFields = possibleFields.filter((f) =>
          !Array.isArray(fields)
            ? f === primaryKey
            : fields.length === 0
              ? f === primaryKey
              : fields.includes(f)
        )
        if (!returnableFields.includes(primaryKey)) {
          returnableFields.unshift(primaryKey)
        }
        const ret = {
          total: Number.isNaN(totalRaw) ? 0 : totalRaw,
          page: validatedMethodOptions.page,
          perPage: validatedMethodOptions.perPage,
          records: rawRecords.map((record) =>
            this.$formatResourcefulRecord(record as any, returnableFields, [], deepRelatedFields)
          ) as Partial<{
            [key: string]: any
          }>[],
          aggregations: getAggregationResults(aggregations, countResults![0]),
          countQuery: countQueryQuery,
          recordsQuery: recordsQueryQuery,
        }
        const paginator = new SimplePaginator(ret.total, ret.perPage, ret.page, ...ret.records)
        await this.$hooks.runner('after:paginate').run(paginator)
        return ret
      }

      /**
       * Retrieves a single model record by its unique identifier with access control.
       *
       * This method implements secure record retrieval by first applying query scope callbacks
       * to verify the record exists within the user's access scope, then fetching the full
       * model instance for field-level ACL evaluation. Only fields that pass ACL checks
       * are included in the response.
       *
       * The dual-fetch pattern ensures security: the first query applies scoping constraints
       * to verify access, while the second query retrieves the full model for ACL evaluation.
       *
       * @param uid - The unique identifier of the record to retrieve
       * @param ctx - HTTP context containing request information and authentication
       * @param app - Application service instance for accessing app-level services
       * @param hooks - Optional array of query scope callbacks to apply additional filtering constraints
       *
       * @returns Promise resolving to the record object with only accessible fields
       *
       * @throws {E_MISSING_PRIMARY_KEY_EXCEPTION} When the model has no identifiable primary key
       * @throws {E_RECORD_NOT_FOUND_EXCEPTION} When no record exists with the given ID or user lacks access
       * @throws {E_FORBIDDEN} When access is denied by model-level or field-level ACL filters
       */
      static async $onResourcefulRead(
        uid: any,
        ctx: HttpContext,
        app: ApplicationService,
        hooks: ResourcefulScopeHooks = []
      ): Promise<ResourcefulModelSerializableAttributes<Model & ResourcefulModel>> {
        /*
        |--------------------------------------------------------------------------
        | Normalize inputs
        |--------------------------------------------------------------------------
        */
        const primaryKey = this.$getPrivateKeyAttribute()

        if (!primaryKey) {
          throw new E_MISSING_PRIMARY_KEY_EXCEPTION(this.$resourcefulName)
        }

        /*
        |--------------------------------------------------------------------------
        | Check Access
        |--------------------------------------------------------------------------
        */
        const query = this.$resourcefulGetQueryBuilder()

        const allScopeCallbacks = [...(this.$resourcefulQueryScopeCallbacks.list || []), ...hooks]

        for (const scopeCallback of allScopeCallbacks) {
          await scopeCallback(ctx, app, query, this)
        }

        const recordRaw = await query.where(this.primaryKey, uid).select(this.primaryKey).first()

        if (!recordRaw) {
          throw new E_RECORD_NOT_FOUND_EXCEPTION()
        }

        const rid = recordRaw[this.primaryKey]

        const recordQuery = this.query().where(this.primaryKey, rid)
        const { possibleRelationships, possibleFields } =
          this.$loadPossibilitiesForResourcefulRecord()
        // Native `preload` (not `preloadCached`): `recordQuery` comes from
        // `this.query()`, which injects the CachingPreloader, so configured 1:1
        // relations cache transparently here; this inline path never needs `forceLoad`.
        possibleRelationships.forEach((prop: any) => {
          recordQuery.preload(prop)
        })
        this.$resourcefulEnforcedPreloads.forEach((prop: string) => {
          if (!possibleRelationships.includes(prop)) {
            const rel = this.$getRelation(prop)
            if (rel) {
              recordQuery.preload(prop as any)
            }
          }
        })
        const record = await recordQuery.first()
        if (!record) {
          throw new E_RECORD_NOT_FOUND_EXCEPTION()
        }

        const { isForbidden, allowedSerializedMap, message } = await this.$resourcefulCheckAccess({
          ctx,
          app,
          instance: record,
          operation: CRUDOperationsEnum.READ,
        })

        if (isForbidden) {
          throw new E_FORBIDDEN(message)
        }

        /*
        |--------------------------------------------------------------------------
        | Format and return the result
        |--------------------------------------------------------------------------
        */
        const enforcedPossibleFields: string[] = []
        if (ctx.request.header('x-resourceful-for') === 'resourceful-ui') {
          /*
          |--------------------------------------------------------------------------
          | Special condition: Resourceful UI Components
          |--------------------------------------------------------------------------
          | Resourceful UI components requires that the same fields as the "write"
          | schema be utilized in addition to the fields for the "read" schema
          | in order to allow forms to show the current values correctly, especially
          | enum and relationship-bound fields.
          */
          this.$resourcefulColumns.forEach((opts, col) => {
            // Map<relationship, title>
            const possible: Map<string, string> = new Map()
            const processRelationship = (
              relation: RelationshipsContract,
              relationshipKey: string
            ) => {
              relation.boot()
              switch (relation.type) {
                case 'hasOne':
                case 'belongsTo': {
                  if (relation.foreignKey === col) {
                    const relationship = this.$resourcefulRelationships.get(relationshipKey)
                    const lucidRelationship = this.$relationsDefinitions.get(relationshipKey)
                    if (!relationship || !lucidRelationship) return
                    const relatedModel = relationship.relatedModel()
                    if (!guards.isResourcefulModel(relatedModel)) return
                    const title = guards.isString(lucidRelationship.serializeAs)
                      ? lucidRelationship.serializeAs || relationshipKey
                      : relationshipKey
                    possible.set(col, title || col)
                  }
                  break
                }
              }
            }
            if (
              'relationshipKey' in opts &&
              'string' === typeof opts.relationshipKey &&
              opts.relationshipKey.trim().length > 0
            ) {
              const lucidRelationship = this.$relationsDefinitions.get(opts.relationshipKey)
              if (this.$resourcefulRelationships.has(opts.relationshipKey) && lucidRelationship) {
                processRelationship(lucidRelationship, opts.relationshipKey)
              }
            } else {
              this.$relationsDefinitions.forEach(processRelationship)
            }
            if (possible.size > 0) {
              if (possible.size > 1) {
                // TODO: provide a warning for the developer that multiple relationships were found
                // for the same column key and that they should specifically define `relationshipKey` in
                // the column definition to avoid ambiguity
              }
              const key = Array.from(possible.keys())[0]
              allowedSerializedMap.set(key, true)
              if (!possibleFields.includes(key)) {
                possibleFields.push(key)
              }
              if (!enforcedPossibleFields.includes(key)) {
                enforcedPossibleFields.push(key)
              }
            }
          })
        }
        return this.$formatResourcefulRecord(
          record as any,
          possibleFields.filter((f) => allowedSerializedMap.get(f) === true),
          enforcedPossibleFields
        ) as ResourcefulModelSerializableAttributes<Model & ResourcefulModel>
      }

      /**
       * Loads a specific relationship for a model record with pagination, filtering, and access control.
       *
       * This method provides paginated relationship loading by leveraging the related model's
       * $onResourcefulIndex method with automatically generated relationship constraints.
       * It supports full filtering, sorting, and pagination capabilities while maintaining
       * proper access control and scoping.
       *
       * The method automatically generates query scope hooks to constrain results to only
       * records related to the specified parent record, preventing unauthorized data access
       * and ensuring proper relationship boundaries are maintained.
       *
       * @param uid - The unique identifier of the parent record
       * @param relationshipKey - The name of the relationship property to load
       * @param filter - Lucene-style query string for filtering related records
       * @param page - The page number for pagination (must be ≥ 1)
       * @param perPage - Number of records per page (must be ≥ 1 and ≤ 100)
       * @param fields - Array of field names to include in the response from the related model
       * @param sort - Array of sort criteria as [field, direction] tuples
       * @param ctx - HTTP context containing request information and authentication
       * @param app - Application service instance for accessing app-level services
       * @param hooks - Optional array of additional query scope callbacks
       *
       * @returns Promise resolving to paginated relationship results with the same structure as $onResourcefulIndex
       *
       * @throws {E_MISSING_PRIMARY_KEY_EXCEPTION} When the model has no identifiable primary key
       * @throws {E_INVALID_RELATIONSHIP_EXCEPTION} When the specified relationship doesn't exist or isn't resourceful
       * @throws {E_FORBIDDEN} When access is denied by model-level or field-level ACL filters
       * @throws {E_INVALID_RESOUREFUL_INDEX_REQUEST_EXCEPTION} When input validation fails
       *
       * @example
       * ```typescript
       * // Load user's posts with filtering and pagination
       * const userPosts = await Post.$onResourcefulReadRelationship(
       *   123,                    // user ID
       *   'posts',               // relationship name
       *   'status:published',    // filter
       *   1,                     // page
       *   10,                    // perPage
       *   ['id', 'title'],       // fields
       *   [['createdAt', 'desc']], // sort
       *   ctx,
       *   app
       * );
       *
       * // Load user's skills (many-to-many)
       * const userSkills = await Skill.$onResourcefulReadRelationship(
       *   123,
       *   'skills',
       *   null,  // no filter
       *   1,
       *   50,
       *   null,  // all fields
       *   null,  // default sort
       *   ctx,
       *   app
       * );
       * ```
       */
      static async $onResourcefulReadRelationship(
        uid: any,
        relationshipKey: string,
        filter: string | null | undefined,
        page: number,
        perPage: number,
        fields: string | string[] | null | undefined,
        sort: Array<[string, 'asc' | 'desc']> | null | undefined,
        ctx: HttpContext,
        app: ApplicationService,
        hooks: ResourcefulScopeHooks = [],
        aggregations: ResourcefulIndexAggregateOptions = {}
      ): Promise<ResourcefulIndexResult<{ [key: string]: any }>> {
        /*
        |--------------------------------------------------------------------------
        | Normalize inputs
        |--------------------------------------------------------------------------
        */
        const primaryKey = this.$getPrivateKeyAttribute()

        if (!primaryKey) {
          throw new E_MISSING_PRIMARY_KEY_EXCEPTION(this.$resourcefulName)
        }

        /*
        |--------------------------------------------------------------------------
        | Validate relationship exists and is accessible
        |--------------------------------------------------------------------------
        */
        const relationshipDefinition = this.$resourcefulRelationships.get(relationshipKey)
        if (!relationshipDefinition) {
          throw new E_INVALID_RELATIONSHIP_EXCEPTION(
            `Relationship '${relationshipKey}' not found on model '${this.$resourcefulName}'`
          )
        }

        const lucidRelationshipDefinition = this.$getRelation(relationshipKey)
        if (!lucidRelationshipDefinition) {
          throw new E_INVALID_RELATIONSHIP_EXCEPTION(
            `Lucid relationship '${relationshipKey}' not found on model '${this.$resourcefulName}'`
          )
        }

        const RelatedModel = relationshipDefinition.relatedModel()
        if (typeof (RelatedModel as any).$onResourcefulIndex !== 'function') {
          throw new E_INVALID_RELATIONSHIP_EXCEPTION(
            `Related model for '${relationshipKey}' does not implement resourceful operations`
          )
        }

        /*
        |--------------------------------------------------------------------------
        | Generate relationship constraint hook
        |--------------------------------------------------------------------------
        */
        const relationshipHook: BoundResourcefulQueryScopeCallback = async (
          uuid: any,
          _hookCtx,
          _hookApp,
          query
        ) => {
          const relationshipType = (lucidRelationshipDefinition as any).type

          switch (relationshipType) {
            case 'hasMany':
              {
                // For hasMany: WHERE foreign_key = uid
                const foreignKey = (lucidRelationshipDefinition as any).options.foreignKey
                if (!foreignKey) {
                  throw new E_INVALID_RELATIONSHIP_EXCEPTION(
                    `HasMany relationship '${relationshipKey}' missing foreignKey`
                  )
                }
                const foreignColumn = RelatedModel.$keys.attributesToColumns.get(foreignKey)
                if (!foreignColumn) {
                  throw new E_INVALID_RELATIONSHIP_EXCEPTION(
                    `Foreign key '${foreignKey}' not found in related model columns`
                  )
                }
                query.where(foreignColumn, uuid)
              }
              break

            case 'manyToMany':
              {
                // For manyToMany: WHERE EXISTS (SELECT 1 FROM pivot_table WHERE ...)
                const pivotTable = (lucidRelationshipDefinition as any).options.pivotTable
                const localKey = (lucidRelationshipDefinition as any).options.localKey
                const relatedKey = (lucidRelationshipDefinition as any).options.relatedKey
                const pivotForeignKey = (lucidRelationshipDefinition as any).options.pivotForeignKey
                const pivotRelatedForeignKey = (lucidRelationshipDefinition as any).options
                  .pivotRelatedForeignKey

                if (
                  !pivotTable ||
                  !localKey ||
                  !relatedKey ||
                  !pivotForeignKey ||
                  !pivotRelatedForeignKey
                ) {
                  throw new E_INVALID_RELATIONSHIP_EXCEPTION(
                    `ManyToMany relationship '${relationshipKey}' missing required pivot configuration`
                  )
                }

                const relatedColumn = RelatedModel.$keys.attributesToColumns.get(relatedKey)
                if (!relatedColumn) {
                  throw new E_INVALID_RELATIONSHIP_EXCEPTION(
                    `Related key '${relatedKey}' not found in related model columns`
                  )
                }

                query.whereExists((subQuery) => {
                  subQuery
                    .from(pivotTable)
                    .select(1)
                    .whereColumn(
                      `${pivotTable}.${pivotRelatedForeignKey}`,
                      `${RelatedModel.table}.${relatedColumn}`
                    )
                    .where(`${pivotTable}.${pivotForeignKey}`, uid)
                })
              }
              break

            case 'hasManyThrough':
              {
                // For hasManyThrough: WHERE EXISTS (SELECT 1 FROM through_table JOIN related_table ...)
                const throughModel = (lucidRelationshipDefinition as any).options.throughModel
                  ? (lucidRelationshipDefinition as any).options.throughModel()
                  : undefined
                const foreignKey =
                  (lucidRelationshipDefinition as any).options.foreignKey || RelatedModel.primaryKey
                const localKey =
                  (lucidRelationshipDefinition as any).options.localKey || this.primaryKey
                const throughForeignKey = (lucidRelationshipDefinition as any).options
                  .throughForeignKey
                const throughLocalKey =
                  (lucidRelationshipDefinition as any).options.throughLocalKey ||
                  throughModel?.primaryKey

                if (
                  !throughModel ||
                  !foreignKey ||
                  !localKey ||
                  !throughForeignKey ||
                  !throughLocalKey
                ) {
                  throw new E_INVALID_RELATIONSHIP_EXCEPTION(
                    `HasManyThrough relationship '${relationshipKey}' missing required through configuration`
                  )
                }

                // Map attribute names to database columns
                // foreignKey: through table's column that matches parent's localKey (e.g., tenantId -> tenant_id)
                const throughForeignKeyColumn =
                  throughModel.$keys.attributesToColumns.get(foreignKey) || foreignKey
                // throughLocalKey: through table's primary key (e.g., id)
                const throughLocalKeyColumn =
                  throughModel.$keys.attributesToColumns.get(throughLocalKey) || throughLocalKey
                // throughForeignKey: related table's column that references through table (e.g., tenantPaymentServiceProviderId -> tenant_payment_service_provider_id)
                const relatedThroughForeignKeyColumn =
                  RelatedModel.$keys.attributesToColumns.get(throughForeignKey) || throughForeignKey

                if (
                  !throughForeignKeyColumn ||
                  !throughLocalKeyColumn ||
                  !relatedThroughForeignKeyColumn
                ) {
                  const missing = [
                    !throughForeignKeyColumn ? 'throughForeignKeyColumn' : undefined,
                    !throughLocalKeyColumn ? 'throughLocalKeyColumn' : undefined,
                    !relatedThroughForeignKeyColumn ? 'relatedThroughForeignKeyColumn' : undefined,
                  ].filter((x) => x !== undefined)
                  throw new E_INVALID_RELATIONSHIP_EXCEPTION(
                    `HasManyThrough relationship '${relationshipKey}' has invalid key mappings. Missing: ${missing.join(', ')}`
                  )
                }

                query.whereExists((subQuery) => {
                  subQuery
                    .from(throughModel.table)
                    .select(1)
                    .where(`${throughModel.table}.${throughForeignKeyColumn}`, uid)
                    .whereColumn(
                      `${throughModel.table}.${throughLocalKeyColumn}`,
                      `${RelatedModel.table}.${relatedThroughForeignKeyColumn}`
                    )
                    .limit(1)
                })
              }
              break

            default:
              throw new E_INVALID_RELATIONSHIP_EXCEPTION(
                `Relationship type '${relationshipType}' is not supported for paginated loading`
              )
          }
        }

        /*
        |--------------------------------------------------------------------------
        | Call the related model's index method with the relationship constraint
        |--------------------------------------------------------------------------
        */
        const allHooks = [relationshipHook.bind(null, uid), ...hooks]

        try {
          return (RelatedModel as any).$onResourcefulIndex(
            filter,
            page,
            perPage,
            fields,
            sort,
            ctx,
            app,
            allHooks,
            aggregations
          )
        } catch (err) {
          if (err instanceof E_INVALID_RESOUREFUL_INDEX_REQUEST_EXCEPTION) {
            throw new E_INVALID_RESOUREFUL_READ_RELATIONSHIP_REQUEST_EXCEPTION(err)
          } else {
            throw err
          }
        }
      }

      /**
       * Creates a new model record with payload validation and access control.
       *
       * This method handles secure record creation by validating the request payload
       * against both model-level and request-specific validation schemas, checking
       * field-level write permissions, and returning the created record with appropriate
       * field filtering applied via the read operation.
       *
       * @param payload - The data object containing field values for the new record
       * @param ctx - HTTP context containing request information and authentication
       * @param app - Application service instance for accessing app-level services
       * @param hooks - Optional array of validation schema getters for additional payload validation
       *
       * @returns Promise resolving to the created record with only accessible fields
       *
       * @throws {E_MISSING_PRIMARY_KEY_EXCEPTION} When the model has no identifiable primary key
       * @throws {E_INVALID_PAYLOAD_EXCEPTION} When core model validation fails
       * @throws {E_FORBIDDEN_PAYLOAD_EXCEPTION} When request-specific validation fails
       * @throws {E_FORBIDDEN} When access is denied by model-level or field-level ACL filters
       */
      static async $onResourcefulCreate(
        payload: any,
        ctx: HttpContext,
        app: ApplicationService,
        hooks: ResourcefulValidationHooks = []
      ): Promise<ResourcefulModelSerializableAttributes<Model & ResourcefulModel>> {
        /*
        |--------------------------------------------------------------------------
        | Normalize inputs
        |--------------------------------------------------------------------------
        */
        const primaryKey = this.$getPrivateKeyAttribute()

        if (!primaryKey) {
          throw new E_MISSING_PRIMARY_KEY_EXCEPTION(this.$resourcefulName)
        }
        /*
        |--------------------------------------------------------------------------
        | Check Access
        |--------------------------------------------------------------------------
        */
        const payloadValidatorGetters = [...(options.payloadValidationSchemaBuilders?.create || [])]
        const payloadSchemaGettersError = await this.$validatePayloadWithPayloadSchemaGetters(
          ctx,
          app,
          payload,
          hooks
        )

        if (payloadSchemaGettersError) {
          throw new E_FORBIDDEN_PAYLOAD_EXCEPTION(payloadSchemaGettersError)
        }

        const { isForbidden, allowedSerializedMap, message } = await this.$resourcefulCheckAccess({
          ctx,
          app,
          operation: CRUDOperationsEnum.CREATE,
        })

        if (isForbidden) {
          throw new E_FORBIDDEN(message)
        }

        /*
        |--------------------------------------------------------------------------
        | Validate and prepare payload
        |--------------------------------------------------------------------------
        */
        const preparedPayload: Record<string, any> = {}
        const resolvedPayload = await this.$resolveStorablePayload(
          ctx,
          app,
          payload,
          payloadValidatorGetters,
          hooks
        )

        // Only process fields that are allowed by ACL
        for (const [key, value] of Object.entries(resolvedPayload)) {
          let attribute = this.$keys.serializedToAttributes.get(key)
          const isValueNull = value === null || value === undefined

          // If attribute is not found, the key might already be the column name
          // for fields with serializeAs: null
          if (!attribute) {
            // Check if this key corresponds to a column that has serializeAs: null
            const columnExists = typeof this.$keys.attributesToColumns.get(key) !== 'undefined'
            if (columnExists) {
              attribute = key // The key is already the attribute/column name
            } else {
              continue // Skip unknown fields
            }
          }

          // Check if this field is allowed - either by serialized name or column name
          const isAllowedBySerializedName = allowedSerializedMap.has(key)
          const isAllowedByColumnName = allowedSerializedMap.has(attribute)
          const isAllowed = isAllowedBySerializedName || isAllowedByColumnName

          if (!isValueNull && !isAllowed) {
            throw new E_FORBIDDEN(`User does not has write access to field ${key}.`)
          }

          preparedPayload[attribute] = value
        }

        /*
        |--------------------------------------------------------------------------
        | Create the record
        |--------------------------------------------------------------------------
        */
        const newRecord = await this.create(preparedPayload as InstanceType<Model>)

        /*
        |--------------------------------------------------------------------------
        | Format and return the result
        |--------------------------------------------------------------------------
        */

        // Call inner Read method to honor Read ACL
        return await this.$onResourcefulRead(newRecord.$getAttribute(primaryKey), ctx, app)
      }

      /**
       * Updates an existing model record with payload validation and access control.
       *
       * This method implements secure record updates by first verifying the record exists
       * and is accessible via the read operation, validating the update payload against both
       * model-level and request-specific schemas, checking field-level write permissions,
       * and returning the updated record with appropriate field filtering applied.
       *
       * The method reuses $onResourcefulRead for consistent access control and scoping logic.
       *
       * @param uid - The unique identifier of the record to update
       * @param payload - The data object containing field values to update
       * @param ctx - HTTP context containing request information and authentication
       * @param app - Application service instance for accessing app-level services
       * @param hooks - Optional object containing query scope callbacks and validation schema getters
       *
       * @returns Promise resolving to the updated record with only accessible fields
       *
       * @throws {E_MISSING_PRIMARY_KEY_EXCEPTION} When the model has no identifiable primary key
       * @throws {E_RECORD_NOT_FOUND_EXCEPTION} When no record exists with the given ID or user lacks access
       * @throws {E_INVALID_PAYLOAD_EXCEPTION} When core model validation fails
       * @throws {E_FORBIDDEN_PAYLOAD_EXCEPTION} When request-specific validation fails
       * @throws {E_FORBIDDEN} When access is denied by model-level or field-level ACL filters
       */
      static async $onResourcefulUpdate(
        uid: any,
        payload: any,
        ctx: HttpContext,
        app: ApplicationService,
        hooks: Partial<ResourcefulHooks> = {}
      ): Promise<ResourcefulModelSerializableAttributes<Model & ResourcefulModel>> {
        /*
        |--------------------------------------------------------------------------
        | Normalize inputs
        |--------------------------------------------------------------------------
        */
        const primaryKey = this.$getPrivateKeyAttribute()

        if (!primaryKey) {
          throw new E_MISSING_PRIMARY_KEY_EXCEPTION(this.$resourcefulName)
        }

        /*
        |--------------------------------------------------------------------------
        | Check Access & Find the existing record
        |--------------------------------------------------------------------------
        */
        const payloadValidatorGetters = [...(options.payloadValidationSchemaBuilders?.update || [])]
        const payloadSchemaGettersError = await this.$validatePayloadWithPayloadSchemaGetters(
          ctx,
          app,
          payload,
          hooks.payloadValidationSchemas || []
        )

        if (payloadSchemaGettersError) {
          throw new E_FORBIDDEN_PAYLOAD_EXCEPTION(payloadSchemaGettersError)
        }

        // Attempt to load the existing record via $onResourcefulRead in order to
        // prevent duplicating the scoping logic
        await this.$onResourcefulRead(uid, ctx, app, hooks.queryScopeCallbacks || [])

        // Refetching record using model to update with it later
        const record = await this.query().where(primaryKey, uid).first()
        if (!record) {
          throw new E_RECORD_NOT_FOUND_EXCEPTION()
        }

        const { isForbidden, allowedSerializedMap, message } = await this.$resourcefulCheckAccess({
          ctx,
          app,
          instance: record,
          operation: CRUDOperationsEnum.UPDATE,
        })

        if (isForbidden) {
          throw new E_FORBIDDEN(message)
        }

        /*
        |--------------------------------------------------------------------------
        | Prepare payload
        |--------------------------------------------------------------------------
        */
        const preparedPayload: Record<string, any> = {}
        const resolvedPayload = await this.$resolveStorablePayload(
          ctx,
          app,
          payload,
          payloadValidatorGetters,
          hooks.payloadValidationSchemas || []
        )

        // Only process fields that are allowed by ACL
        for (const [serializedKey, value] of Object.entries(resolvedPayload)) {
          let attribute = this.$keys.serializedToAttributes.get(serializedKey)
          const isValueNull = value === null || value === undefined

          // If attribute is not found, the key might already be the column name
          // for fields with serializeAs: null
          if (!attribute) {
            // Check if this key corresponds to a column that has serializeAs: null
            const columnExists =
              typeof this.$keys.attributesToColumns.get(serializedKey) !== 'undefined'
            if (columnExists) {
              attribute = serializedKey // The key is already the attribute/column name
            } else {
              continue // Skip unknown fields
            }
          }

          // Check if this field is allowed - either by serialized name or column name
          const isAllowedBySerializedName = allowedSerializedMap.has(serializedKey)
          const isAllowedByColumnName = allowedSerializedMap.has(attribute)
          const isAllowed = isAllowedBySerializedName || isAllowedByColumnName

          if (!isValueNull && !isAllowed) {
            throw new E_FORBIDDEN(`User does not has write access to field ${attribute}.`)
          }

          preparedPayload[attribute] = value
        }

        /*
        |--------------------------------------------------------------------------
        | Update the record
        |--------------------------------------------------------------------------
        */

        record.merge(preparedPayload as Partial<InstanceType<Model>>)

        await record.save()

        /*
        |--------------------------------------------------------------------------
        | Format and return the result
        |--------------------------------------------------------------------------
        */

        // Call inner Read method to honor Read ACL
        return this.$onResourcefulRead(uid, ctx, app)
      }

      /**
       * Deletes an existing model record with verification and access control.
       *
       * This method securely deletes records by first verifying the record exists
       * and is accessible via the read operation, then checking model-level deletion
       * permissions before executing the deletion. Returns the record as it existed
       * before deletion with appropriate field filtering applied.
       *
       * The method reuses $onResourcefulRead for consistent access control and scoping logic.
       *
       * @param uid - The unique identifier of the record to delete
       * @param ctx - HTTP context containing request information and authentication
       * @param app - Application service instance for accessing app-level services
       * @param hooks - Optional object containing query scope callbacks
       *
       * @returns Promise resolving to the deleted record as it existed before deletion
       *
       * @throws {E_MISSING_PRIMARY_KEY_EXCEPTION} When the model has no identifiable primary key
       * @throws {E_RECORD_NOT_FOUND_EXCEPTION} When no record exists with the given ID or user lacks access
       * @throws {E_FORBIDDEN} When access is denied by model-level access control filters
       */
      static async $onResourcefulDelete(
        uid: any,
        ctx: HttpContext,
        app: ApplicationService,
        hooks: ResourcefulScopeHooks = []
      ): Promise<void> {
        /*
        |--------------------------------------------------------------------------
        | Normalize inputs
        |--------------------------------------------------------------------------
        */
        const primaryKey = this.$getPrivateKeyAttribute()

        if (!primaryKey) {
          throw new E_MISSING_PRIMARY_KEY_EXCEPTION(this.$resourcefulName)
        }

        /*
        |--------------------------------------------------------------------------
        | Find the existing record for access check
        |--------------------------------------------------------------------------
        */
        const query = this.$resourcefulGetQueryBuilder()

        for (const scopeCallback of [
          ...(this.$resourcefulQueryScopeCallbacks.access || []),
          ...hooks,
        ]) {
          await scopeCallback(ctx, app, query, this)
        }

        const rawRecord = await query.where(primaryKey, uid).first()

        if (!rawRecord) {
          throw new E_RECORD_NOT_FOUND_EXCEPTION()
        }
        const { possibleFields } = this.$loadPossibilitiesForResourcefulRecord()
        const record = new this().merge(this.$formatResourcefulRecord(rawRecord, possibleFields))

        /*
        |--------------------------------------------------------------------------
        | Check Access
        |--------------------------------------------------------------------------
        */
        const { isForbidden, message } = await this.$resourcefulCheckAccess({
          ctx,
          app,
          instance: record,
          operation: CRUDOperationsEnum.DELETE,
        })

        if (isForbidden) {
          throw new E_FORBIDDEN(message)
        }

        /*
        |--------------------------------------------------------------------------
        | Delete the record
        |--------------------------------------------------------------------------
        */

        await record.delete()
      }

      static async $onResourcefulBulkUpdate(
        filter: string | null | undefined,
        payload: any,
        ctx: HttpContext,
        app: ApplicationService,
        hooks: Partial<ResourcefulHooks> = {}
      ): Promise<
        | {
            [key: string]: ResourcefulModelSerializableAttributes<Model & ResourcefulModel> | Error
          }
        | Error
      > {
        const primaryKey = this.$getPrivateKeyAttribute()

        if (!primaryKey) {
          throw new E_MISSING_PRIMARY_KEY_EXCEPTION(this.$resourcefulName)
        }
        /*
        |--------------------------------------------------------------------------
        | Turn $onResourcefulIndex into an async iterator for bulk updates
        |--------------------------------------------------------------------------
        */
        const ctor = this
        async function* resourcefulIndexIteratorForUpdate(f: string | null | undefined) {
          let page = 1
          let pages = 0
          const pk = ctor.$getPrivateKeyAttribute()
          if (!pk) return
          const whileAbortController = new AbortController()
          while ((pages === 0 || page <= pages) && whileAbortController.signal.aborted === false) {
            try {
              const result = await ctor.$onResourcefulIndex(
                f,
                page,
                100,
                [pk],
                [[pk, 'asc']],
                ctx,
                app,
                hooks.queryScopeCallbacks || []
              )
              if (result.total === 0) {
                // No records found, break the loop
                whileAbortController.abort()
                break
              }
              pages = Math.ceil(result.total / result.perPage)
              page++
              yield result.records
            } catch (err) {
              // break early on error
              whileAbortController.abort()
              if (err instanceof Error) {
                throw err
              } else {
                throw new E_BULK_UPDATE_SEARCH_UNKNOWN_EXCEPTION()
              }
            }
          }
        }
        const uids: Set<number | string> = new Set()
        for await (const records of resourcefulIndexIteratorForUpdate(filter)) {
          for (const record of records) {
            const uid = record[primaryKey]
            if (uid !== undefined && uid !== null) {
              uids.add(uid)
            }
          }
        }
        return await this.$onResourcefulBulkUpdateByUid(Array.from(uids), payload, ctx, app, hooks)
      }

      static async $onResourcefulBulkUpdateByUid(
        uids: (number | string)[],
        payload: any,
        ctx: HttpContext,
        app: ApplicationService,
        hooks?: Partial<ResourcefulHooks>,
        concurrency = 5
      ): Promise<{
        [key: string]: ResourcefulModelSerializableAttributes<Model & ResourcefulModel> | Error
      }> {
        const results: {
          [key: string]: ResourcefulModelSerializableAttributes<Model & ResourcefulModel> | Error
        } = {}
        /*
        |--------------------------------------------------------------------------
        | Iterate through records for bulk update using pMap
        | to balance concurrency and performance
        |--------------------------------------------------------------------------
        */
        /* istanbul ignore next -- @preserve */
        await pMap(
          uids,
          async (uid) => {
            try {
              results[uid] = await this.$onResourcefulUpdate(uid, payload, ctx, app, hooks)
            } catch (err) {
              const e = err instanceof Error ? err : new Error('Unknown error during update')
              results[uid] = e
            }
          },
          { concurrency }
        )
        return results
      }
    }
    Object.defineProperty(ResourcefulModel, '$resourcefulName', {
      value: validatedOptions.name,
      writable: false,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(ResourcefulModel, '$resourcefulTitleColumn', {
      value: validatedOptions.resourcefulTitleColumn,
      writable: false,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(ResourcefulModel, '$resourcefulMetaDescription', {
      value: validatedOptions.description,
      writable: false,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(ResourcefulModel, '$resourcefulMetaExternalDocs', {
      value: validatedOptions.externalDocs,
      writable: false,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(ResourcefulModel, '$resourcefulMetaExample', {
      value: validatedOptions.example,
      writable: false,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(ResourcefulModel, '$resourcefulAccessControlFilters', {
      value: validatedOptions.accessControlFilters,
      writable: false,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(ResourcefulModel, '$resourcefulOnACLError', {
      value: validatedOptions.onACLError,
      writable: false,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(ResourcefulModel, '$resourcefulQueryScopeCallbacks', {
      value: validatedOptions.queryScopeCallbacks || { list: [], access: [] },
      writable: false,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(ResourcefulModel, '$resourcefulPayloadValidationSchemaBuilders', {
      value: validatedOptions.payloadValidationSchemaBuilders || { create: [], update: [] },
      writable: false,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(ResourcefulModel, '$resourcefulEnforcedPreloads', {
      value: new Set(validatedOptions.enforcedPreloads || []),
      writable: false,
      enumerable: true,
      configurable: false,
    })
    return ResourcefulModel
  }
}
